import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { catchError, debounceTime, filter, map, Observable, of, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import saveAs from 'file-saver';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DeepPartial, Theme } from '../../shared-services/theme.types';
import { ThemeService } from '../../shared-services/theme.service';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { commaSeparatedValue, oneDecimalWithCommas } from '../../utils/formatting.utils';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../../shared-services/environment-config.token';

type SmartTableColumnType =
  | 'text'
  | 'number'
  | 'currency'
  | 'slider'
  | 'progress'
  | 'status'
  | 'dropdown'
  | 'action'
  | 'history';
type SmartTableAlign = 'left' | 'center' | 'right';
type SmartTableScope = 'main' | 'popup' | 'inline';

interface SmartTableOption {
  label: string;
  value: any;
  disabled?: boolean;
}

interface SmartTablePopupAction {
  label: string;
  actionType?: 'navigate' | 'export';
  navigateAction?: Record<string, any>;
  closeOnClick?: boolean;
  fileName?: string;
}

interface SmartTablePopupConfig {
  displayMode?: 'popup' | 'inline';
  height?: string | number;
  title?: string;
  apiUrl?: string;
  method?: string;
  rows?: any[];
  rowsPath?: string;
  dataKey?: string;
  rowSortKey?: string;
  rowSortDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  orderKey?: string;
  orderDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  responsePath?: string;
  columns?: SmartTableColumn[];
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  headerValues?: Record<string, string>;
  headerActions?: SmartTablePopupAction[];
  tableHeight?: string | number;
  emptyStateMessage?: string;
  showColumnDividers?: boolean;
  dividerColumns?: string[];
  submitAction?: SmartTableSubmitActionConfig;
  requestPayloadMode?: 'auto' | 'custom' | 'merge' | 'none';
  requestPayload?: any;
  queryParams?: Record<string, any>;
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  apaRangeMinKey?: string;
  apaRangeMaxKey?: string;
  apaRangeStepKey?: string;
  apaRangeTargetColumnKey?: string;
  apaRangeProductTypeKey?: string;
  hideInlineEditToggle?: boolean;
  enableInlineEditModeByDefault?: boolean;
}

interface SmartTableRowPopupConfig extends SmartTablePopupConfig {
  enabled?: boolean;
  rowIndexes?: Array<number | 'n' | 'N'>;
  rowIds?: Array<string | number>;
  rowIdKey?: string;
  matchKey?: string;
  matchValue?: any;
  matchValues?: any[];
}

interface SmartTableSubmitActionConfig {
  label?: string;
  apiUrl?: string;
  method?: string;
  requestPayload?: any;
  customPayload?: any;
  skipDefaultPayload?: boolean;
  queryParams?: Record<string, any>;
  autoSubmitOnEdit?: boolean;
  commitOnBlur?: boolean;
  triggerColumns?: string[];
  showButton?: boolean;
  debounceMs?: number;
  syncFiltersOnEdit?: boolean;
  clearFiltersOnSubmitSuccess?: boolean;
  refreshOnSubmitSuccess?: boolean;
  refreshFilterListener?: string | string[];
  filterListener?: string | string[];
  filterKeys?: string[];
  navigateAction?: Record<string, any>;
  postSubmitActions?: SmartTableSubmitActionConfig[];
  successMessage?: string;
  errorMessage?: string;
  responseMessagePath?: string;
  align?: 'start' | 'center' | 'end';
}

// Data shape for the history hover-popover. Deliberately separate from the component/
// rendering so a future API response only needs to be mapped into this shape — nothing
// about the popover markup or logic has to change.
interface SmartTableHistoryChange {
  field: string;
  previousValue: string;
  newValue: string;
}

interface SmartTableHistoryVersion {
  versionNo: number;
  updatedBy: string;
  updatedDate: string;
  changes: SmartTableHistoryChange[];
}

interface SmartTableHistoryPopoverConfig {
  customerId: string;
  updatedBy: string;
  updatedDate: string;
  versions: SmartTableHistoryVersion[];
}

interface SmartTableHistoryApiConfig {
  apiUrl?: string;
  entityName?: string;
  attributesList?: string[];
  filterAttributeName?: string;
  filterOperator?: string;
  syncBeforeFetch?: boolean;
  displayFields?: string[];
  fieldLabels?: Record<string, string>;
  displayChangedFieldsOnly?: boolean;
  maxVersions?: number;
}

interface SmartTableColumn {
  key: string;
  bindingKey?: string;
  header?: string;
  headerKey?: string;
  type?: SmartTableColumnType;
  editable?: boolean;
  editOnClick?: boolean;
  headerEditIndicator?: boolean;
  showInlineEditToggle?: boolean;
  autoEditWhenEmpty?: boolean;
  // Static placeholder text shown in a floating popover on hover. No API wiring yet —
  // once a data source exists, this can be resolved dynamically per row instead.
  hoverPopoverContent?: string;
  // Structured history hover-popover (version cards with a changed-field diff table).
  // Mock data for now; later this can come from an API response mapped into the same shape.
  historyPopover?: SmartTableHistoryPopoverConfig;
  // Row field to show as the popover's heading instead of the literal word "History" —
  // e.g. "customer_name" so the popup reads as "TLD ACE" for that row.
  historyPopoverTitleKey?: string;
  historyPopoverTitleField?: string;
  historyApi?: SmartTableHistoryApiConfig;
  dropdownCommitOnEnter?: boolean;
  width?: string;
  widthWeight?: number;
  align?: SmartTableAlign;
  headerAlign?: SmartTableAlign;
  headerTextAlign?: SmartTableAlign;
  // Native title-attribute tooltip shown on the header label, the value cell, and (for
  // editable columns) the input and the header's edit-toggle pencil — e.g. explaining
  // that "Price Change %" here is only editable at the engine level. Purely a hint
  // attribute: doesn't change layout, styling, or column behavior.
  tooltip?: string;
  placeholder?: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  progressValueKey?: string;
  progressMax?: number;
  progressMaxKey?: string;
  progressColor?: string;
  progressColorKey?: string;
  statusColor?: string;
  statusColorKey?: string;
  compactFormat?: boolean;
  compactFormatKey?: string;
  compactUnit?: 'auto' | 'k' | 'm';
  compactUnitKey?: string;
  compactDecimals?: number;
  options?: any[];
  dropdownApiUrl?: string;
  dropdownMethod?: string;
  dropdownResponsePath?: string;
  dropdownLabelKey?: string;
  dropdownValueKey?: string;
  dropdownDisabledKey?: string;
  dropdownPlaceholder?: string;
  dropdownCurrentValueFirst?: boolean;
  dropdownRequestPayload?: any;
  dropdownQueryParams?: Record<string, any>;
  valueApiUrl?: string;
  valueApiMethod?: string;
  valueApiRequestConfig?: any;
  valueApiResponsePath?: string;
  valueApiRowMatchKey?: string;
  valueApiResponseMatchKey?: string;
  valueApiResponseValueKey?: string;
  valueApiResponseValueMap?: Record<string, string>;
  valueApiUseFirstRecord?: boolean;
  hideValueWhenKey?: string;
  hideValueWhenValue?: any;
  actionLabel?: string;
  actionVariant?: 'button' | 'icon';
  actionTooltip?: string;
  navigateAction?: Record<string, any>;
  popup?: SmartTablePopupConfig;
  editAction?: SmartTableSubmitActionConfig;
}

interface SmartTableFormulaConfig {
  confidenceKey?: string;
  priceLiftKey?: string;
  riskKey?: string;
  resourceLoadKey?: string;
  baseValueKey?: string;
}

interface PendingAutoSubmitEdit {
  scope: SmartTableScope;
  rowIndex: number;
  row: any;
  column: SmartTableColumn;
}

interface PendingColumnEditAction {
  scope: SmartTableScope;
  rowIndex: number;
  row: any;
  column: SmartTableColumn;
  event?: any;
}

interface PendingSubmitRowEdit {
  scope: SmartTableScope;
  rowIndex: number;
  row: any;
  column: SmartTableColumn;
}

interface SmartTableCurrentUserContext {
  userId: string;
  username: string;
  email: string;
}

interface SmartTableRecalculationConfig {
  enabled?: boolean;
  triggerAfterChanges?: number | string | string[] | {
    count?: number;
    columnKeys?: string[];
    keys?: string[];
    columns?: string[];
    bindingKeys?: string[];
  };
  watchColumns?: string[];
  apiUrl?: string;
  method?: string;
  responsePath?: string;
  formula?: SmartTableFormulaConfig;
  inputKeys?: {
    confidence?: string;
    priceLift?: string;
    risk?: string;
    resourceLoad?: string;
    baseRevenue?: string;
  };
  scoreKey?: string;
  scoreColorKey?: string;
  projectionKey?: string;
  statusKey?: string;
  statusColorKey?: string;
  syncKey?: string;
  syncColorKey?: string;
  outputKeys?: {
    score?: string;
    scoreColor?: string;
    projection?: string;
    status?: string;
    statusColor?: string;
    syncState?: string;
    syncColor?: string;
  };
  syncStateColors?: {
    pending?: string;
    success?: string;
    error?: string;
  };
}

interface SmartTableConfig {
  widgetId?: string;
  title?: string;
  apiUrl?: string;
  method?: string;
  configUrl?: string;
  mockDataUrl?: string;
  height?: string | number;
  fontScale?: number;
  columnBindings?: Record<string, Partial<SmartTableColumn>>;
  rows?: any[];
  rowsPath?: string;
  dataKey?: string;
  rowSortKey?: string;
  rowSortDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  orderKey?: string;
  orderDirection?: 'asc' | 'desc' | 'ascending' | 'descending' | string;
  columns?: SmartTableColumn[];
  headerAlign?: SmartTableAlign;
  headerTextAlign?: SmartTableAlign;
  headerSingleLine?: boolean;
  compactTable?: boolean;
  fitToContainer?: boolean;
  nowrapData?: boolean;
  headerValues?: Record<string, string>;
  tableHeight?: string | number;
  emptyStateMessage?: string;
  showColumnDividers?: boolean;
  dividerColumns?: string[];
  headerActions?: SmartTablePopupAction[];
  rowPopup?: SmartTableRowPopupConfig;
  themeOverrides?: DeepPartial<Theme>;
  recalculation?: SmartTableRecalculationConfig;
  submitAction?: SmartTableSubmitActionConfig;
  filterListener?: string | string[];
  filterListner?: string | string[];
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  apaRangeMinKey?: string;
  apaRangeMaxKey?: string;
  apaRangeStepKey?: string;
  apaRangeTargetColumnKey?: string;
  apaRangeProductTypeKey?: string;
}

interface RecalculationBand {
  min?: number;
  label?: string;
  color?: string;
}

interface RecalculationRules {
  weights?: Record<string, number>;
  projectionConfidenceBonus?: number;
  projectionRiskPenalty?: number;
  projectionLoadPenalty?: number;
  syncMessage?: string;
  statusBands?: RecalculationBand[];
}

interface SmartTableViewState {
  scope: SmartTableScope;
  title: string;
  columns: SmartTableColumn[];
  rows: any[];
  loading: boolean;
  errorMessage: string;
  tableHeight: string;
  emptyStateMessage: string;
  headerValues?: Record<string, string>;
  headerActions?: SmartTablePopupAction[];
  showColumnDividers?: boolean;
  dividerColumns?: string[];
  submitAction?: SmartTableSubmitActionConfig;
}

interface SmartTableTemplateContext {
  row?: any;
  editedRow?: any;
  editedRows?: any[];
  editedValue?: any;
  column?: SmartTableColumn;
  event?: any;
}

@Component({
  selector: 'app-json-driven-smart-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './json-driven-smart-table.component.html',
  styleUrl: './json-driven-smart-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonDrivenSmartTableComponent implements OnInit, OnDestroy {
  private static readonly DEBUG_KEY = '[PAT-SLIDER-TRACE-20260527]';
  private static readonly APA_OVERRIDES_STORAGE_KEY = 'dvm_apa_range_overrides';
  private readonly destroy$ = new Subject<void>();
  private readonly recalculationTrigger$ = new Subject<void>();
  private initialized = false;
  private inputData: SmartTableConfig | null = null;
  private readonly dropdownOptionsCache = new Map<string, SmartTableOption[]>();
  private readonly columnValueApiCache = new Map<string, Observable<any>>();
  private readonly dropdownLoadingKeys = new Set<string>();
  private autoSubmitTimer?: ReturnType<typeof setTimeout>;
  private pendingAutoSubmit = false;
  private rowLoadVersion = 0;

  @Input() set data(value: SmartTableConfig | null) {
    this.inputData = value;
    if (this.initialized) {
      this.initializeComponent();
    }
  }

  get data(): SmartTableConfig | null {
    return this.inputData;
  }

  loading = false;
  apiSyncing = false;
  errorMessage = '';
  syncMessage = '';
  tableHeight = '28rem';
  columns: SmartTableColumn[] = [];
  rows: any[] = [];
  widgetConfig: SmartTableConfig | null = null;
  editedCellKeys = new Set<string>();
  lastEditedRow: any = null;

  popupOpen = false;
  popupTable: SmartTableViewState = this.createEmptyTableState('popup');
  popupContextRow: any = null;
  popupContextColumn: SmartTableColumn | undefined;
  inlineDetailRowId: string | null = null;
  inlineDetailTable: SmartTableViewState = this.createEmptyTableState('inline');
  inlineContextRow: any = null;
  inlineContextColumn: SmartTableColumn | undefined;
  // Captured just before a data reload (e.g. a "refreshDashboardOnSuccess" edit) flips the
  // `*ngIf` that wraps `.table-scroll`, which destroys/recreates that element and would
  // otherwise silently reset scrollTop to 0 once the reload finishes.
  private pendingScrollRestoreTop: number | null = null;
  // Columns configured with editable:true inside an inline detail popup (e.g. "Price Change %")
  // stay read-only until the user explicitly clicks the edit toggle — resets to off each time
  // the popup opens/closes so it never leaks edit mode across different customers' rows.
  inlineDetailEditModeEnabled = false;
  inlineDetailHideEditToggle = false;
  activeInlineEditColumnKey: string | null = null;
  activeEditableCellKey: string | null = null;
  activeEditableColumnKeys = new Set<string>();
  // Cells opened automatically because their value started empty (see autoEditWhenEmpty).
  // Tracked separately from activeEditableCellKey since multiple empty rows can be open
  // at once, unlike the single click-activated cell.
  private autoOpenEmptyCellKeys = new Set<string>();
  hoverPopoverKey: string | null = null;
  hoverPopoverPosition: { top: number; left?: number; right?: number } | null = null;
  hoverPopoverMaxHeight: number | null = null;
  private pinnedHoverPopoverKey: string | null = null;
  private collapsedHistoryVersionKeys = new Set<string>();
  private pendingAutoSubmitEdits = new Map<string, PendingAutoSubmitEdit>();
  private pendingColumnEditActions = new Map<string, PendingColumnEditAction>();
  private pendingSubmitRowEdits = new Map<string, PendingSubmitRowEdit>();
  private pendingInlineDetailScrollRestore = false;

  submitInFlight = false;
  submitMessage = '';
  submitErrorMessage = '';
  private historyApiCache = new Map<string, SmartTableHistoryPopoverConfig>();
  private historyApiLoading = new Set<string>();
  private readonly environmentConfig = inject<EnvironmentConfig | null>(ENVIRONMENT_CONFIG, { optional: true });

  get headerSingleLine(): boolean {
    return this.widgetConfig?.headerSingleLine === true;
  }

  get compactTable(): boolean {
    return this.widgetConfig?.compactTable === true;
  }

  get fitToContainer(): boolean {
    return this.widgetConfig?.fitToContainer === true;
  }

  get nowrapData(): boolean {
    return this.widgetConfig?.nowrapData === true;
  }

  get userInputEnabled(): boolean {
    return this.toBooleanFlag(this.environmentConfig?.USER_INPUT_ENABLED) ?? true;
  }

  constructor(
    private configService: ConfigService,
    private chartFilterHandler: ChartFilterHandlerService,
    private globalFilterService: GlobalFilterService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private hostElement: ElementRef<HTMLElement>,
    private router: Router,
    private keycloakService: KeycloakService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyResolvedTheme();
      this.cdr.markForCheck();
    });

    this.responsiveLayoutService.reflow$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (!this.widgetConfig?.widgetId || !this.widgetConfig) {
          return;
        }

        const shouldReload = this.chartFilterHandler.shouldReload(filters, this.widgetConfig.widgetId);
        this.debug('filter$ event', {
          widgetId: this.widgetConfig.widgetId,
          shouldReload,
          filters: this.summarizeFilters(filters),
        });

        if (!shouldReload) {
          return;
        }

        this.loadRowsFromConfig(this.widgetConfig, filters);
      });

    this.recalculationTrigger$
      .pipe(
        debounceTime(250),
        filter(() => this.shouldRunRecalculation()),
        switchMap(() => this.runRecalculationRequest()),
        takeUntil(this.destroy$),
      )
      .subscribe((rules) => {
        if (rules) {
          this.applyRecalculationRules(rules);
        }
      });

    this.initializeComponent();
  }

  ngOnDestroy(): void {
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
      this.autoSubmitTimer = undefined;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get editedCellCount(): number {
    return this.editedCellKeys.size;
  }

  get editTriggerCount(): number {
    const triggerConfig = this.widgetConfig?.recalculation?.triggerAfterChanges;
    if (typeof triggerConfig === 'number') {
      return Math.max(1, Number(triggerConfig || 3));
    }

    const objectCount = Number(
      triggerConfig && typeof triggerConfig === 'object' && !Array.isArray(triggerConfig)
        ? triggerConfig.count
        : undefined,
    );
    if (Number.isFinite(objectCount) && objectCount > 0) {
      return Math.max(1, objectCount);
    }

    const configuredColumns = this.getTriggerColumnKeys();
    if (configuredColumns.length > 0) {
      return configuredColumns.length;
    }

    return 3;
  }

  get emptyStateMessage(): string {
    return this.widgetConfig?.emptyStateMessage || 'No table rows available.';
  }

  get hasSubmitAction(): boolean {
    return this.userInputEnabled && !!this.widgetConfig?.submitAction && this.widgetConfig?.submitAction?.showButton !== false;
  }

  get mainHeaderActions(): SmartTablePopupAction[] {
    const actions = Array.isArray(this.widgetConfig?.headerActions) ? this.widgetConfig?.headerActions : [];
    return actions
      .map((action) => ({
        ...action,
        label: String(this.resolveDynamicValue(action.label || '', {}) || ''),
        actionType: String(this.resolveDynamicValue(action.actionType || '', {}) || '').toLowerCase() === 'export'
          ? 'export' as const
          : 'navigate' as const,
        fileName: this.resolveExportFileName(action.fileName, this.widgetConfig?.title || this.widgetConfig?.widgetId || 'table-export'),
      }))
      .filter((action) => !!action.label);
  }

  get submitButtonLabel(): string {
    return this.widgetConfig?.submitAction?.label || 'Submit';
  }

  get submitButtonAlignClass(): string {
    const align = this.widgetConfig?.submitAction?.align || 'end';
    return `align-${align}`;
  }

  getRowClasses(scope: SmartTableScope, row: any, rowIndex: number): Record<string, boolean> {
    return {
      'clickable-row': this.hasRowPopup(scope, row, rowIndex),
    };
  }

  trackByColumn = (_: number, column: SmartTableColumn): string => column.bindingKey || column.key;
  trackByRow = (_: number, row: any): string => this.getRowId(row, _);

  initializeComponent(): void {
    if (!this.initialized || !this.inputData) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.syncMessage = '';
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.submitInFlight = false;
    this.columns = [];
    this.rows = [];
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.inlineDetailRowId = null;
    this.inlineDetailTable = this.createEmptyTableState('inline');
    this.inlineContextRow = null;
    this.inlineContextColumn = undefined;
    this.dropdownOptionsCache.clear();
    this.dropdownLoadingKeys.clear();
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
      this.autoSubmitTimer = undefined;
    }
    this.pendingAutoSubmit = false;
    this.editedCellKeys.clear();
    this.lastEditedRow = null;
    this.cdr.markForCheck();

    const hasInlineConfig = Array.isArray(this.inputData.columns) || Array.isArray(this.inputData.rows);
    const configUrl = this.inputData.configUrl || (hasInlineConfig ? '' : this.inputData.apiUrl || '');

    if (!configUrl) {
      const mergedConfig = this.mergeWidgetConfigs(null, this.inputData);
      this.applyWidgetConfig(mergedConfig);
      return;
    }

    this.configService
      .getConfig(configUrl, this.inputData, undefined, this.resolveMethod(configUrl))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (remoteConfig) => this.applyWidgetConfig(this.mergeWidgetConfigs(remoteConfig, this.inputData!)),
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load table configuration.';
          this.cdr.markForCheck();
        },
      });
  }

  applyWidgetConfig(config: SmartTableConfig): void {
    this.widgetConfig = config;
    this.columns = this.normalizeColumns(config);
    this.tableHeight = this.resolveConfiguredTableHeight(config, '28rem');
    this.applyResolvedTheme();

    if (this.columns.length === 0) {
      this.loading = false;
      this.errorMessage = 'No table columns were configured. Define columns in dashboard JSON or table config.';
      this.cdr.markForCheck();
      return;
    }

    this.loadRowsFromConfig(config, this.getInitialFiltersForWidget(config));
  }

  getHeaderLabel(column: SmartTableColumn, scope: SmartTableScope = 'main'): string {
    const headerKey = column.headerKey || '';
    const headerValues = scope === 'popup' ? this.popupTable.headerValues : this.widgetConfig?.headerValues;
    const configuredHeader = headerKey ? headerValues?.[headerKey] : '';
    return configuredHeader || column.header || this.humanize(column.key);
  }

  isSliderColumn(column: SmartTableColumn): boolean {
    return column.type === 'slider';
  }

  isReadonlySlider(column: SmartTableColumn): boolean {
    return this.isSliderColumn(column) && column.editable !== true;
  }

  isProgressColumn(column: SmartTableColumn): boolean {
    return column.type === 'progress';
  }

  isStatusColumn(column: SmartTableColumn): boolean {
    return column.type === 'status';
  }

  isDropdownColumn(column: SmartTableColumn): boolean {
    return column.type === 'dropdown';
  }

  isActionColumn(column: SmartTableColumn): boolean {
    return column.type === 'action' || column.type === 'history';
  }

  private isHistoryColumn(column: SmartTableColumn): boolean {
    return column.type === 'history';
  }

  hasPopup(column: SmartTableColumn): boolean {
    return !!column.popup;
  }

  isEditableColumn(column: SmartTableColumn, scope: SmartTableScope = 'main'): boolean {
    if (scope === 'inline') {
      if (column.showInlineEditToggle === true) {
        return column.editable === true && this.activeInlineEditColumnKey === (column.bindingKey || column.key);
      }

      if (column.editOnClick === true || column.headerEditIndicator === true) {
        return column.editable === true;
      }

      if (!this.inlineDetailEditModeEnabled) {
        return false;
      }
    }

    return column.editable === true;
  }

  getColumnTooltip(column: SmartTableColumn): string {
    return this.normalizeTooltipText(column?.tooltip);
  }

  isPopupTriggerColumn(column: SmartTableColumn): boolean {
    return this.hasPopup(column);
  }

  isNavigableDisplayColumn(column: SmartTableColumn, scope: SmartTableScope = 'main'): boolean {
    return !!column?.navigateAction &&
      !this.isActionColumn(column) &&
      !this.isPopupTriggerColumn(column) &&
      !this.isEditableColumn(column, scope) &&
      !this.isDropdownColumn(column) &&
      !this.isSliderColumn(column);
  }

  getCellAlignment(column: SmartTableColumn): SmartTableAlign {
    return column.align || (this.isNumberLikeColumn(column) ? 'right' : 'left');
  }

  getHeaderAlignment(column: SmartTableColumn, scope: SmartTableScope = 'main'): SmartTableAlign {
    return (
      column.headerAlign ||
      column.headerTextAlign ||
      (scope === 'main' ? this.widgetConfig?.headerAlign || this.widgetConfig?.headerTextAlign : undefined) ||
      'left'
    );
  }

  getHeaderClasses(column: SmartTableColumn, scope: SmartTableScope = 'main'): Record<string, boolean> {
    const alignment = this.getHeaderAlignment(column, scope);
    return {
      'align-left': alignment === 'left',
      'align-center': alignment === 'center',
      'align-right': alignment === 'right',
      'column-divider': this.showDivider(column, scope),
    };
  }

  getCellClasses(column: SmartTableColumn, scope: SmartTableScope = 'main'): Record<string, boolean> {
    const alignment = this.getCellAlignment(column);
    return {
      'align-left': alignment === 'left',
      'align-center': alignment === 'center',
      'align-right': alignment === 'right',
      'column-divider': this.showDivider(column, scope),
      'editable-cell': this.isEditableColumn(column, scope),
      'slider-column': this.isSliderColumn(column),
      'progress-column': this.isProgressColumn(column),
      'status-column': this.isStatusColumn(column),
      'dropdown-column': this.isDropdownColumn(column),
      'action-column': this.isActionColumn(column),
      'popup-trigger-column': this.isPopupTriggerColumn(column),
      'navigable-column': this.isNavigableDisplayColumn(column, scope),
    };
  }

  showDivider(column: SmartTableColumn, scope: SmartTableScope = 'main'): boolean {
    const dividerColumns = this.getDividerColumns(scope);
    const columnKey = String(column.bindingKey || column.key || '').trim();

    if (dividerColumns.length > 0) {
      return dividerColumns.includes(columnKey);
    }

    return this.getShowColumnDividers(scope);
  }

  getColumnWidth(column: SmartTableColumn): string | null {
    if (this.fitToContainer) {
      return this.getFitToContainerColumnWidth(column);
    }

    return column.width || null;
  }

  getTableMinWidth(columns: SmartTableColumn[]): string | null {
    if (this.fitToContainer) {
      return null;
    }

    if (!Array.isArray(columns) || columns.length === 0) {
      return null;
    }

    const totalWidth = columns.reduce((sum, column) => sum + this.resolveColumnPixelWidth(column.width), 0);
    if (totalWidth <= 0) {
      return null;
    }

    return `${Math.max(totalWidth + 100, 640)}px`;
  }

  private getFitToContainerColumnWidth(column: SmartTableColumn): string | null {
    const weight = Number(column?.widthWeight);
    const totalWeight = this.getFitToContainerTotalWeight();

    if (!Number.isFinite(weight) || weight <= 0 || totalWeight <= 0) {
      return null;
    }

    return `${(weight / totalWeight) * 100}%`;
  }

  private getFitToContainerTotalWeight(): number {
    return (this.columns || []).reduce((sum, column) => {
      const weight = Number(column?.widthWeight);
      return sum + (Number.isFinite(weight) && weight > 0 ? weight : 1);
    }, 0);
  }

  getDisplayValue(row: any, column: SmartTableColumn): string {
    const key = String(column?.key || '').trim();
    const bindingKey = String(column?.bindingKey || '').trim();

    if (!key && !bindingKey) {
      return '';
    }

    if (this.shouldHideDisplayValue(row, column)) {
      return '-';
    }

    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    if (value && typeof value === 'object') {
      return '';
    }

    if (column.type === 'currency') {
      return this.formatCurrencyValue(value, column, row);
    }

    if (column.type === 'number' || this.isNumberLikeColumn(column)) {
      return this.formatNumericValue(value, column, row);
    }

    return `${column.prefix || ''}${String(value)}${column.suffix || ''}`;
  }

  private shouldHideDisplayValue(row: any, column: SmartTableColumn): boolean {
    const conditionKey = String(column?.hideValueWhenKey || '').trim();
    if (!conditionKey) {
      return false;
    }

    const actual = this.getValueByPath(row, conditionKey);
    const expected = column?.hideValueWhenValue;
    return this.normalizeDropdownComparable(actual) === this.normalizeDropdownComparable(expected);
  }

  getInputValue(row: any, column: SmartTableColumn): string | number {
    const value = this.getValueByPath(row, column.key);
    if (value === undefined || value === null) {
      return '';
    }
    return value as string | number;
  }

  onInputChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (this.isClickToEditColumn(column) && !this.showHeaderEditIndicator(column)) {
      this.activeEditableCellKey = this.buildEditableCellKey(scope, rowIndex, row, column);
    }

    const input = event.target as HTMLInputElement;
    const nextValue = this.isNumberLikeColumn(column)
      ? this.normalizeNumericColumnValue(this.parseNumber(input.value), column)
      : input.value;
    row[column.key] = nextValue;

    if (column.editAction?.apiUrl) {
      this.queuePendingColumnEditAction(scope, rowIndex, row, column);
    } else if (scope === 'main' && this.shouldQueueMainSubmitRow(column)) {
      this.queuePendingSubmitRowEdit(scope, rowIndex, row, column);
    }

    if (scope !== 'main' && this.shouldAutoSubmitColumn(column, scope)) {
      this.queuePendingAutoSubmitEdit(scope, rowIndex, row, column);
    }
  }

  onInputCommit(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
    event?: Event,
    trigger: 'change' | 'enter' = 'enter',
  ): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (trigger === 'change' && !this.shouldCommitInputOnBlur(scope, column)) {
      return;
    }

    if (trigger === 'enter') {
      event?.preventDefault?.();
      event?.stopPropagation?.();
    }

    this.registerEdit(scope, rowIndex, row, column);
    this.clearPendingColumnEditAction(scope, rowIndex, row, column);
    this.clearPendingSubmitRowEdit(scope, rowIndex, row, column);
    if (trigger === 'enter' && this.shouldAutoSubmitColumn(column, scope)) {
      this.flushAutoSubmit(scope);
    }
    if (column.showInlineEditToggle === true) {
      this.activeInlineEditColumnKey = null;
    }
    if (this.isClickToEditColumn(column)) {
      this.activeEditableCellKey = null;
      this.activeEditableColumnKeys.delete(this.buildEditableColumnKey(scope, column));
    }
    if (column.autoEditWhenEmpty === true) {
      this.autoOpenEmptyCellKeys.delete(this.buildEditableCellKey(scope, rowIndex, row, column));
    }
  }

  onSliderChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    if (!this.userInputEnabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    row[column.key] = this.normalizeNumericColumnValue(this.parseNumber(input.value), column);

    if (column.editAction?.apiUrl) {
      this.queuePendingColumnEditAction(scope, rowIndex, row, column);
    } else if (scope === 'main' && this.shouldQueueMainSubmitRow(column)) {
      this.queuePendingSubmitRowEdit(scope, rowIndex, row, column);
    }

    if (scope !== 'main' && this.shouldAutoSubmitColumn(column, scope)) {
      this.queuePendingAutoSubmitEdit(scope, rowIndex, row, column);
    }
  }

  onSliderCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    if (!this.userInputEnabled) {
      return;
    }

    this.registerEdit(scope, rowIndex, row, column);
    this.clearPendingSubmitRowEdit(scope, rowIndex, row, column);
  }

  onDropdownFocus(rowIndex: number, row: any, column: SmartTableColumn): void {
    this.ensureDropdownOptions(rowIndex, row, column);
  }

  onDropdownChange(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    if (!this.userInputEnabled) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    const resolvedValue = this.resolveDropdownValue(select.value, rowIndex, row, column);
    const valueKey = String(column.key || '').trim();
    const bindingKey = String(column.bindingKey || '').trim();

    if (valueKey) {
      row[valueKey] = resolvedValue;
    }

    if (bindingKey && bindingKey !== valueKey) {
      row[bindingKey] = resolvedValue;
    }

    if (valueKey === 'status' || bindingKey === 'status') {
      row['planning_status'] = resolvedValue;
    }

    if (column.editAction?.apiUrl) {
      this.queuePendingColumnEditAction(scope, rowIndex, row, column, {
        value: select.value,
        target: select,
        rawEvent: event,
      });
    } else if (scope === 'main' && this.shouldQueueMainSubmitRow(column)) {
      this.queuePendingSubmitRowEdit(scope, rowIndex, row, column);
    } else if (scope !== 'main' && this.shouldAutoSubmitColumn(column, scope)) {
      this.queuePendingAutoSubmitEdit(scope, rowIndex, row, column);
    }

    if (column.dropdownCommitOnEnter === true) {
      this.cdr.markForCheck();
      return;
    }

    this.registerEdit(scope, rowIndex, row, column, {
      value: select.value,
      target: select,
      rawEvent: event,
    });
  }

  onDropdownCommit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: Event): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (column.dropdownCommitOnEnter !== true) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    this.registerEdit(scope, rowIndex, row, column, {
      value: select?.value,
      target: select,
      rawEvent: event,
    });
    this.clearPendingColumnEditAction(scope, rowIndex, row, column);
    this.clearPendingSubmitRowEdit(scope, rowIndex, row, column);
    if (scope !== 'main' && this.shouldAutoSubmitColumn(column, scope)) {
      this.flushAutoSubmit(scope);
    }
  }

  getDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): SmartTableOption[] {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0) {
      return this.prioritizeCurrentDropdownOption(staticOptions, row, column);
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.prioritizeCurrentDropdownOption(this.dropdownOptionsCache.get(cacheKey) || [], row, column);
  }

  isDropdownLoading(rowIndex: number, row: any, column: SmartTableColumn): boolean {
    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    return this.dropdownLoadingKeys.has(cacheKey);
  }

  getDropdownValue(row: any, column: SmartTableColumn): string {
    const valuePath = String(column.bindingKey || column.key || '').trim();
    const value = this.getValueByPath(row, valuePath);
    if (value === undefined || value === null) {
      return '';
    }

    const rawValue = String(value).trim();
    const staticOptions = this.extractStaticOptions(column);
    const matchedStaticOption = staticOptions.find((option) =>
      this.normalizeDropdownComparable(option.value) === this.normalizeDropdownComparable(rawValue) ||
      this.normalizeDropdownComparable(option.label) === this.normalizeDropdownComparable(rawValue),
    );

    return matchedStaticOption ? String(matchedStaticOption.value) : rawValue;
  }

  getActionLabel(row: any, column: SmartTableColumn): string {
    const configured = this.resolveDynamicValue(column.actionLabel, { row, column });
    if (configured !== undefined && configured !== null && configured !== '') {
      return String(configured);
    }

    if (this.isHistoryColumn(column)) {
      return "<span class='material-icons'>history</span>";
    }

    if (this.isActionColumn(column)) {
      return 'Open';
    }

    return this.getDisplayValue(row, column);
  }

  getActionTooltip(row: any, column: SmartTableColumn): string {
    const configured = this.resolveDynamicValue(column.actionTooltip, { row, column });
    const tooltip = this.normalizeTooltipText(configured);
    return tooltip || (this.isHistoryColumn(column) ? 'View history' : '');
  }

  hasHoverPopover(column: SmartTableColumn): boolean {
    return (column.hoverPopoverContent !== undefined && column.hoverPopoverContent !== null) || this.hasHistoryPopover(column);
  }

  hasHistoryPopover(column: SmartTableColumn): boolean {
    return !!column.historyPopover || !!column.historyApi;
  }

  getHistoryPopover(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): SmartTableHistoryPopoverConfig | null {
    return this.historyApiCache.get(this.buildEditableCellKey(scope, rowIndex, row, column)) || column.historyPopover || null;
  }

  isHistoryPopoverLoading(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): boolean {
    return this.historyApiLoading.has(this.buildEditableCellKey(scope, rowIndex, row, column));
  }

  getHistoryPopoverTitle(row: any, column: SmartTableColumn): string {
    const key = column.historyPopoverTitleKey || column.historyPopoverTitleField;
    if (!key) {
      return 'History';
    }
    const value = this.getValueByPath(row, key);
    return value !== undefined && value !== null && value !== '' ? String(value) : 'History';
  }

  closeHoverPopover(): void {
    this.hoverPopoverKey = null;
    this.pinnedHoverPopoverKey = null;
    this.hoverPopoverPosition = null;
    this.hoverPopoverMaxHeight = null;
    this.cdr.markForCheck();
  }

  isLatestHistoryVersion(popover: SmartTableHistoryPopoverConfig, version: SmartTableHistoryVersion): boolean {
    return popover.versions[0] === version;
  }

  isHistoryVersionExpanded(column: SmartTableColumn, version: SmartTableHistoryVersion): boolean {
    return !this.collapsedHistoryVersionKeys.has(this.buildHistoryVersionKey(column, version));
  }

  toggleHistoryVersion(column: SmartTableColumn, version: SmartTableHistoryVersion, event?: Event): void {
    event?.stopPropagation();
    const key = this.buildHistoryVersionKey(column, version);
    if (this.collapsedHistoryVersionKeys.has(key)) {
      this.collapsedHistoryVersionKeys.delete(key);
    } else {
      this.collapsedHistoryVersionKeys.add(key);
    }
    this.cdr.markForCheck();
  }

  private buildHistoryVersionKey(column: SmartTableColumn, version: SmartTableHistoryVersion): string {
    return `${column.bindingKey || column.key}::${version.versionNo}`;
  }

  private loadHistoryPopover(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    const cellKey = this.buildEditableCellKey(scope, rowIndex, row, column);
    if (!column.historyApi || this.historyApiLoading.has(cellKey)) {
      return;
    }

    this.historyApiLoading.add(cellKey);
    this.historyApiCache.delete(cellKey);

    const filterField = column.historyApi.filterAttributeName || 'customer_name';
    const filterValue = this.getValueByPath(row, filterField) ?? this.getHistoryPopoverTitle(row, column);
    const payload = {
      attributesList: column.historyApi.attributesList || ['history'],
      filterList: [
        {
          operator: column.historyApi.filterOperator || '=',
          attributeName: filterField,
          attributeValue: filterValue,
          logicalOperator: '',
        },
      ],
      entityName: column.historyApi.entityName,
    };

    this.configService
      .getConfig(
        column.historyApi.apiUrl || '/spriced/platform/getData',
        {
          skipDefaultPayload: true,
          customPayload: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        },
        { requestParams: {}, queryParams: {} },
        'post',
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.historyApiLoading.delete(cellKey);
          const mapped = this.mapHistoryApiResponse(response, column);
          if (mapped) {
            this.historyApiCache.set(cellKey, mapped);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.historyApiLoading.delete(cellKey);
          this.cdr.markForCheck();
        },
      });
  }

  private mapHistoryApiResponse(response: any, column?: SmartTableColumn): SmartTableHistoryPopoverConfig | null {
    const entries = this.extractHistoryEntries(response);
    if (!entries.length) {
      return null;
    }

    const sorted = [...entries]
      .filter((entry) => entry && typeof entry === 'object' && entry.DATA)
      .sort((left, right) => Number(right?.Version_no ?? 0) - Number(left?.Version_no ?? 0));
    const latest = sorted[0];
    if (!latest) {
      return null;
    }

    const maxHistoryVersions = Math.max(1, Number(column?.historyApi?.maxVersions ?? 3));
    const versions = sorted.map((entry, index) => {
      const previousEntry = sorted[index + 1];
      const diffChanges = previousEntry
        ? this.diffHistorySnapshots(previousEntry.DATA, entry.DATA, column)
        : this.buildSnapshotHistoryChanges(entry.DATA, column);
      return {
        versionNo: Number(entry?.Version_no ?? 0),
        updatedBy: String(entry?.Updated_by ?? entry.DATA?.updated_by ?? ''),
        updatedDate: String(entry?.Updated_date ?? entry.DATA?.updated_date ?? ''),
        changes: previousEntry || column?.historyApi?.displayChangedFieldsOnly === true
          ? diffChanges
          : this.buildSnapshotHistoryChanges(entry.DATA, column),
      };
    }).filter((version) => version.changes.length > 0).slice(0, maxHistoryVersions);

    if (!versions.length) {
      return null;
    }

    return {
      customerId: String(latest.DATA?.one_bms_code ?? latest.DATA?.onebms_cust_code ?? latest.DATA?.customer_id ?? ''),
      updatedBy: String(latest.Updated_by ?? latest.DATA?.updated_by ?? ''),
      updatedDate: String(latest.Updated_date ?? latest.DATA?.updated_date ?? ''),
      versions,
    };
  }

  private extractHistoryEntries(response: any): any[] {
    return this.findHistoryEntries(response) || [];
  }

  private findHistoryEntries(source: any): any[] | null {
    if (!source) {
      return null;
    }

    if (Array.isArray(source)) {
      if (source.some((item) => item && typeof item === 'object' && item.DATA)) {
        return source;
      }

      for (const item of source) {
        const nestedHistory = this.findHistoryEntries(item);
        if (nestedHistory?.length) {
          return nestedHistory;
        }
      }

      return null;
    }

    if (typeof source !== 'object') {
      return null;
    }

    if (Array.isArray(source.history)) {
      return source.history;
    }

    const knownContainers = [source.responseList, source.data];
    for (const container of knownContainers) {
      const nestedHistory = this.findHistoryEntries(container);
      if (nestedHistory?.length) {
        return nestedHistory;
      }
    }

    return null;
  }

  private buildSnapshotHistoryChanges(data: Record<string, any>, column?: SmartTableColumn): SmartTableHistoryChange[] {
    return this.getHistoryDisplayKeys(data, column)
      .map((key) => ({
        field: this.formatHistoryFieldLabel(key, column),
        previousValue: this.formatHistoryValue(data[key]),
        newValue: '-',
      }));
  }

  private diffHistorySnapshots(previousData: Record<string, any>, latestData: Record<string, any>, column?: SmartTableColumn): SmartTableHistoryChange[] {
    const keys = this.getHistoryDisplayKeys(latestData, column);
    const changes: SmartTableHistoryChange[] = [];
    keys.forEach((key) => {
      const previousValue = this.getHistoryFieldValue(previousData, key, column);
      const latestValue = this.getHistoryFieldValue(latestData, key, column);
      if (this.normalizeHistoryValue(previousValue) === this.normalizeHistoryValue(latestValue)) {
        return;
      }
      changes.push({
        field: this.formatHistoryFieldLabel(key, column),
        previousValue: this.formatHistoryValue(previousValue),
        newValue: this.formatHistoryValue(latestValue),
      });
    });
    return changes;
  }

  private getHistoryDisplayKeys(data: Record<string, any>, column?: SmartTableColumn): string[] {
    const configuredFields = column?.historyApi?.displayFields;
    const candidates = Array.isArray(configuredFields) && configuredFields.length > 0
      ? configuredFields.filter((field) => this.hasHistoryFieldValue(data, field, column))
      : Object.keys(data || {});

    const seenLabels = new Set<string>();
    return candidates.filter((key) => {
      if (this.isIgnoredHistoryField(key)) {
        return false;
      }
      const label = this.formatHistoryFieldLabel(key, column).toLowerCase();
      if (seenLabels.has(label)) {
        return false;
      }
      seenLabels.add(label);
      return true;
    });
  }

  private hasHistoryFieldValue(data: Record<string, any>, key: string, column?: SmartTableColumn): boolean {
    return this.getHistoryFieldAliases(key, column).some((candidate) => Object.prototype.hasOwnProperty.call(data || {}, candidate));
  }

  private getHistoryFieldValue(data: Record<string, any>, key: string, column?: SmartTableColumn): any {
    const aliases = this.getHistoryFieldAliases(key, column);
    const source = data || {};
    const matchedKey = aliases.find((candidate) => Object.prototype.hasOwnProperty.call(source, candidate));
    return matchedKey ? source[matchedKey] : undefined;
  }

  private getHistoryFieldAliases(key: string, column?: SmartTableColumn): string[] {
    const configuredFields = column?.historyApi?.displayFields || [];
    const configuredLabels = column?.historyApi?.fieldLabels || {};
    const label = configuredLabels[key];
    const labelAliases = label
      ? configuredFields.filter((field) => configuredLabels[field] === label)
      : [];
    const statusAliases = ['status', 'planning_status'];
    const priceChangeAliases = [
      'pricing_lever',
      'price_change_percentage',
      'price_change_percentage_planned',
      'planned_price_change',
      'planned_price_change_percentage',
      'total_price_change_percentage',
      'total_planned_price_change_percentage',
      'drilldown_total_price_change_percentage',
    ];
    const forecastedVolumeAliases = ['forecasted_volume', 'forecastedVolume', 'total_volume_cust_plan'];
    const knownAliases = statusAliases.includes(key)
      ? statusAliases
      : priceChangeAliases.includes(key)
        ? priceChangeAliases
        : forecastedVolumeAliases.includes(key)
          ? forecastedVolumeAliases
          : [];
    return Array.from(new Set([key, ...labelAliases, ...knownAliases]));
  }

  private isIgnoredHistoryField(key: string): boolean {
    return new Set([
      'created_by',
      'created_date',
      'updated_by',
      'updated_date',
      'version_no',
      'is_current',
      'mark_for_deletion',
      'uuid',
      'uid',
      'customer_name',
      'one_bms_code',
      'onebms_cust_code',
      'customer_id',
      'operation',
    ]).has(key);
  }

  private normalizeHistoryValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  private formatHistoryValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (typeof value === 'number') {
      return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
    }
    return String(value);
  }

  private formatHistoryFieldLabel(key: string, column?: SmartTableColumn): string {
    const configuredLabel = column?.historyApi?.fieldLabels?.[key];
    if (configuredLabel) {
      return configuredLabel;
    }

    return String(key || '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatHistoryDate(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }
    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }

  onCellMouseEnter(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event: MouseEvent): void {
    if (!this.hasHoverPopover(column)) {
      return;
    }
    const key = this.buildEditableCellKey(scope, rowIndex, row, column);
    if (this.pinnedHoverPopoverKey && this.pinnedHoverPopoverKey !== key) {
      return;
    }
    this.hoverPopoverKey = key;
    if (column.historyApi) {
      this.loadHistoryPopover(scope, rowIndex, row, column);
    }
    const wrapper = event.currentTarget as HTMLElement;
    const anchor = wrapper?.getBoundingClientRect();
    if (anchor) {
      // position:fixed anchored to the icon's viewport rect — deliberately NOT position:absolute,
      // since the popover would otherwise be clipped by the table's own scroll container
      // whenever it extends above/below that container's visible bounds.
      const margin = 12;
      // Never let the popover rise above the table itself — otherwise, for rows near the top,
      // it would cover the filter bar / summary tiles that sit above the table.
      const tableTop = wrapper.closest('.table-scroll')?.getBoundingClientRect().top ?? margin;
      const minTop = Math.max(margin, tableTop);
      // Cap the popover's own height to whatever room actually exists below minTop — clamping
      // *position* alone isn't enough: if the content is taller than the available space, the
      // box still gets pinned at minTop and its bottom simply runs off-screen, unreachable by
      // scrolling (scroll only reveals content within the box's own on-screen bounds).
      this.hoverPopoverMaxHeight = Math.max(160, window.innerHeight - minTop - margin);
      if (this.hasHistoryPopover(column)) {
        this.hoverPopoverPosition = {
          top: minTop,
          right: window.innerWidth - anchor.left + 14,
        };
      } else {
        const maxLeft = Math.max(margin, window.innerWidth - 260);
        this.hoverPopoverPosition = {
          top: minTop,
          left: Math.min(Math.max(anchor.left - 150, margin), maxLeft),
        };
      }
      if (typeof window !== 'undefined') {
        window.setTimeout(() => this.recenterHoverPopover(anchor, minTop, margin), 0);
      }
    }
    this.cdr.markForCheck();
  }

  private recenterHoverPopover(anchor: DOMRect, minTop: number, margin: number): void {
    if (!this.hoverPopoverPosition) {
      return;
    }
    const el = document.querySelector('.cell-hover-popover') as HTMLElement | null;
    if (!el) {
      return;
    }
    const height = el.getBoundingClientRect().height;
    const maxTop = Math.max(minTop, window.innerHeight - height - margin);
    const desiredTop = anchor.top + anchor.height / 2 - height / 2;
    const clampedTop = Math.min(Math.max(desiredTop, minTop), maxTop);
    if (Math.abs(clampedTop - this.hoverPopoverPosition.top) > 0.5) {
      this.hoverPopoverPosition = { ...this.hoverPopoverPosition, top: clampedTop };
      this.cdr.markForCheck();
    }
  }

  onCellMouseLeave(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): void {
    if (!this.hasHoverPopover(column)) {
      return;
    }
    const key = this.buildEditableCellKey(scope, rowIndex, row, column);
    if (this.pinnedHoverPopoverKey === key) {
      return;
    }
    if (this.hoverPopoverKey === key) {
      this.hoverPopoverKey = null;
      this.hoverPopoverPosition = null;
      this.hoverPopoverMaxHeight = null;
      this.cdr.markForCheck();
    }
  }

  isHoverPopoverActive(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn): boolean {
    return this.hasHoverPopover(column) && this.hoverPopoverKey === this.buildEditableCellKey(scope, rowIndex, row, column);
  }

  getHoverPopoverContent(row: any, column: SmartTableColumn): string {
    const configured = this.resolveDynamicValue(column.hoverPopoverContent, { row, column });
    return String(configured ?? '');
  }

  private normalizeTooltipText(value: any): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '';
    }

    const normalized = text.toLowerCase();
    return normalized === 'null' || normalized === 'undefined' ? '' : text;
  }

  onActionClick(scope: SmartTableScope, row: any, column: SmartTableColumn, rowIndex?: number, event?: Event): void {
    event?.stopPropagation();
    const filters = this.globalFilterService.getCurrentFilters?.() || {};

    if (this.hasHistoryPopover(column)) {
      this.togglePinnedHistoryPopover(scope, rowIndex ?? 0, row, column, event);
      return;
    }

    if (column.popup) {
      this.openPopup(row, column, rowIndex, event);
      return;
    }

    if (column.navigateAction) {
      const navigateAction = this.withResolvedProductPriceType(column.navigateAction, row, filters);
      executeNavigationFromConfig(
        this.router,
        { navigateAction, widgetId: this.widgetConfig?.widgetId },
        { name: column.key, value: this.getActionLabel(row, column) },
        { row, column, filters },
      );
    }
  }

  private togglePinnedHistoryPopover(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
    event?: Event,
  ): void {
    const key = this.buildEditableCellKey(scope, rowIndex, row, column);
    if (this.pinnedHoverPopoverKey === key) {
      this.closeHoverPopover();
      return;
    }

    this.pinnedHoverPopoverKey = null;
    if (event instanceof MouseEvent) {
      this.onCellMouseEnter(scope, rowIndex, row, column, event);
    } else {
      this.hoverPopoverKey = key;
    }
    this.pinnedHoverPopoverKey = key;
    this.cdr.markForCheck();
  }

  onDisplayCellClick(row: any, column: SmartTableColumn, rowIndex?: number, event?: Event, scope: SmartTableScope = 'main'): void {
    event?.stopPropagation();
    const filters = this.globalFilterService.getCurrentFilters?.() || {};

    if (!this.isNavigableDisplayColumn(column, scope)) {
      return;
    }

    const navigateAction = this.withResolvedProductPriceType(column.navigateAction, row, filters);
    executeNavigationFromConfig(
      this.router,
      { navigateAction, widgetId: this.widgetConfig?.widgetId },
      { name: column.bindingKey || column.key, value: this.getDisplayValue(row, column) },
      { row, column, rowIndex, filters },
    );
  }

  hasRowPopup(scope: SmartTableScope, row: any, rowIndex: number): boolean {
    return !!this.resolveRowPopupConfig(scope, row, rowIndex);
  }

  onRowClick(scope: SmartTableScope, row: any, rowIndex: number, event: Event): void {
    const popupConfig = this.resolveRowPopupConfig(scope, row, rowIndex);
    if (!popupConfig || this.isInteractiveTarget(event.target)) {
      return;
    }

    this.openDetailFromConfig(
      popupConfig.displayMode === 'inline' ? 'inline' : 'popup',
      row,
      rowIndex,
      undefined,
      popupConfig,
      event,
    );
  }

  openPopup(row: any, column: SmartTableColumn, rowIndex?: number, event?: Event): void {
    const popupConfig = column.popup;
    if (!popupConfig) {
      return;
    }

    this.openDetailFromConfig(
      popupConfig.displayMode === 'inline' ? 'inline' : 'popup',
      row,
      rowIndex ?? 0,
      column,
      popupConfig,
      event,
    );
  }

  onPopupActionClick(action: SmartTablePopupAction): void {
    this.onDetailActionClick(action, 'popup');
  }

  onInlineActionClick(action: SmartTablePopupAction): void {
    this.onDetailActionClick(action, 'inline');
  }

  onMainActionClick(action: SmartTablePopupAction): void {
    this.onDetailActionClick(action, 'main');
  }

  isInlineDetailRowExpanded(row: any, rowIndex: number): boolean {
    return this.inlineDetailRowId === this.getRowId(row, rowIndex);
  }

  closeInlineDetail(): void {
    this.inlineDetailRowId = null;
    this.inlineDetailTable = this.createEmptyTableState('inline');
    this.inlineContextRow = null;
    this.inlineContextColumn = undefined;
    this.inlineDetailEditModeEnabled = false;
    this.inlineDetailHideEditToggle = false;
    this.activeInlineEditColumnKey = null;
    this.activeEditableCellKey = null;
    Array.from(this.activeEditableColumnKeys)
      .filter((key) => key.startsWith('inline::'))
      .forEach((key) => this.activeEditableColumnKeys.delete(key));
    this.cdr.markForCheck();
  }

  toggleInlineDetailEditMode(column?: SmartTableColumn): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (column?.showInlineEditToggle === true) {
      const nextKey = column.bindingKey || column.key;
      this.activeInlineEditColumnKey = this.activeInlineEditColumnKey === nextKey ? null : nextKey;
      this.activeEditableCellKey = null;
      this.cdr.markForCheck();
      return;
    }

    if (this.inlineDetailHideEditToggle) {
      return;
    }

    this.inlineDetailEditModeEnabled = !this.inlineDetailEditModeEnabled;
    this.cdr.markForCheck();
  }

  isInlineHeaderEditToggleActive(column: SmartTableColumn): boolean {
    if (column.showInlineEditToggle === true) {
      return this.activeInlineEditColumnKey === (column.bindingKey || column.key);
    }

    return this.inlineDetailEditModeEnabled;
  }

  showInlineHeaderEditToggle(scope: SmartTableScope, column: SmartTableColumn): boolean {
    if (!this.userInputEnabled) {
      return false;
    }

    if (scope !== 'inline' || column.editable !== true || this.isDropdownColumn(column)) {
      return false;
    }

    if (column.showInlineEditToggle === true) {
      return true;
    }

    if (column.showInlineEditToggle === false) {
      return false;
    }

    return !this.inlineDetailHideEditToggle;
  }

  showHeaderEditIndicator(column: SmartTableColumn): boolean {
    return this.userInputEnabled && column.headerEditIndicator === true;
  }

  shouldRenderEditableInput(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): boolean {
    if (!this.isEditableColumn(column, scope)) {
      return false;
    }

    if (!this.isClickToEditColumn(column)) {
      return true;
    }

    if (this.isHeaderEditColumnActive(scope, column)) {
      return true;
    }

    const cellKey = this.buildEditableCellKey(scope, rowIndex, row, column);

    if (column.autoEditWhenEmpty === true) {
      if (this.autoOpenEmptyCellKeys.has(cellKey)) {
        return true;
      }
      if (this.isClickToEditValueEmpty(row, column)) {
        this.autoOpenEmptyCellKeys.add(cellKey);
        return true;
      }
    }

    return this.activeEditableCellKey === cellKey;
  }

  isClickToEditColumn(column: SmartTableColumn): boolean {
    return column.editOnClick === true;
  }

  isClickToEditValueEmpty(row: any, column: SmartTableColumn): boolean {
    const rawValue = this.getValueByPath(row, column.key);
    return rawValue === undefined || rawValue === null || String(rawValue).trim() === '';
  }

  activateEditableCell(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
    event?: Event,
  ): void {
    if (!this.isClickToEditColumn(column) || !this.isEditableColumn(column, scope)) {
      return;
    }

    event?.stopPropagation();
    this.activeEditableCellKey = this.buildEditableCellKey(scope, rowIndex, row, column);
    this.cdr.markForCheck();
  }

  toggleHeaderEditColumn(scope: SmartTableScope, column: SmartTableColumn, event?: Event): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (!column.headerEditIndicator || !this.isClickToEditColumn(column)) {
      return;
    }

    event?.stopPropagation();
    const columnKey = this.buildEditableColumnKey(scope, column);

    if (this.activeEditableColumnKeys.has(columnKey)) {
      this.activeEditableColumnKeys.delete(columnKey);
    } else {
      this.activeEditableColumnKeys.add(columnKey);
    }

    this.activeEditableCellKey = null;
    this.cdr.markForCheck();
  }

  isHeaderEditColumnActive(scope: SmartTableScope, column: SmartTableColumn): boolean {
    return this.activeEditableColumnKeys.has(this.buildEditableColumnKey(scope, column));
  }

  getClickToEditDisplayValue(row: any, column: SmartTableColumn): string {
    // getDisplayValue() renders a "-" placeholder glyph for an empty value (fine for a plain
    // read-only cell), but that's not what we want here — check the raw underlying value so an
    // empty click-to-edit box shows this column's own placeholder text (or nothing) instead of "-".
    const rawValue = this.getValueByPath(row, column.key);
    if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') {
      return column.placeholder || '';
    }

    return this.getDisplayValue(row, column);
  }

  private onDetailActionClick(action: SmartTablePopupAction, target: 'main' | 'popup' | 'inline'): void {
    if (!action) {
      return;
    }

    if ((action.actionType || '').toLowerCase() === 'export') {
      this.exportTable(target, action);
      if (target === 'popup' && action.closeOnClick !== false) {
        this.closePopup();
      }
      return;
    }

    if (!action.navigateAction) {
      return;
    }
    const filters = this.globalFilterService.getCurrentFilters?.() || {};

    const contextRow = target === 'popup' ? this.popupContextRow : target === 'inline' ? this.inlineContextRow : null;
    const contextColumn = target === 'popup' ? this.popupContextColumn : target === 'inline' ? this.inlineContextColumn : undefined;
    const navigateAction = this.withResolvedProductPriceType(action.navigateAction, contextRow, filters);

    executeNavigationFromConfig(
      this.router,
      { navigateAction, widgetId: this.widgetConfig?.widgetId },
      { name: 'popup-action', value: action.label },
      { row: contextRow, column: contextColumn, filters },
    );

    if (target === 'popup' && action.closeOnClick !== false) {
      this.closePopup();
    }
  }

  private openDetailFromConfig(
    target: 'popup' | 'inline',
    row: any,
    rowIndex: number,
    column: SmartTableColumn | undefined,
    popupConfig: SmartTablePopupConfig,
    event?: Event,
  ): void {
    event?.stopPropagation();

    const rowId = this.getRowId(row, rowIndex);
    if (target === 'inline' && this.inlineDetailRowId === rowId) {
      this.closeInlineDetail();
      return;
    }

    if (target === 'popup') {
      this.popupContextRow = row;
      this.popupContextColumn = column;
    } else {
      this.inlineContextRow = row;
      this.inlineContextColumn = column;
      this.inlineDetailRowId = rowId;
      this.inlineDetailHideEditToggle = popupConfig.hideInlineEditToggle === true;
      this.inlineDetailEditModeEnabled = popupConfig.enableInlineEditModeByDefault === true;
      this.activeInlineEditColumnKey = null;
    }

    const popupColumns = this.normalizeColumns({
      columns: popupConfig.columns || [],
      columnBindings: popupConfig.columnBindings || {},
    });

    const headerActions = this.resolvePopupHeaderActions(popupConfig, row, column);

    const initialState: SmartTableViewState = {
      scope: target,
      title: String(this.resolveDynamicValue(popupConfig.title || this.getHeaderLabel(column || { key: 'details' } as SmartTableColumn), { row, column }) || ''),
      columns: popupColumns,
      rows: [],
      loading: true,
      errorMessage: '',
      tableHeight: this.resolveConfiguredTableHeight(popupConfig, '22rem'),
      emptyStateMessage: popupConfig.emptyStateMessage || 'No popup rows available.',
      headerValues: popupConfig.headerValues,
      headerActions,
      showColumnDividers: popupConfig.showColumnDividers === true,
      dividerColumns: Array.isArray(popupConfig.dividerColumns) ? popupConfig.dividerColumns.map((value) => String(value || '').trim()).filter(Boolean) : [],
      submitAction: popupConfig.submitAction,
    };

    if (target === 'popup') {
      this.popupOpen = true;
      this.popupTable = initialState;
    } else {
      this.inlineDetailTable = initialState;
      this.scheduleInlineDetailScroll();
    }
    this.cdr.markForCheck();

    const inlineRows = this.extractRowsFromSource(popupConfig.rows, popupConfig);
    if (inlineRows.length > 0) {
      this.resolvePopupRowsForDisplay(inlineRows, popupConfig)
        .pipe(takeUntil(this.destroy$))
        .subscribe((rows) => {
          this.updateDetailTableState(target, {
            rows,
            loading: false,
          });
          if (target === 'inline') {
            this.scheduleInlineDetailScroll();
          }
          this.cdr.markForCheck();
        });
      return;
    }

    const rawApiUrl = popupConfig.apiUrl || '';
    if (!rawApiUrl) {
      this.updateDetailTableState(target, {
        loading: false,
        errorMessage: popupColumns.length === 0 ? 'No popup columns were configured.' : '',
      });
      this.cdr.markForCheck();
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(rawApiUrl, { row, column }) || '');
    const method = this.resolveMethod(apiUrl, popupConfig.method);
    const responsePath = String(this.resolveDynamicValue(popupConfig.responsePath, { row, column }) || '');
    const queryParams = this.resolveDynamicValue(popupConfig.queryParams, { row, column });
    const requestPayload = this.resolvePopupRequestPayload(apiUrl, popupConfig, row, column);
    const filters = this.buildRequestFilters(requestPayload, queryParams);

    this.configService
      .getConfig(apiUrl, popupConfig, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          const extractedRows = this.extractRowsFromSource(source, popupConfig);
          this.resolvePopupRowsForDisplay(extractedRows, popupConfig)
            .pipe(takeUntil(this.destroy$))
            .subscribe((rows) => {
              this.updateDetailTableState(target, {
                rows,
                loading: false,
                errorMessage:
                  popupColumns.length === 0
                    ? 'No popup columns were configured.'
                    : '',
              });
              if (target === 'inline') {
                this.scheduleInlineDetailScroll();
              }
              this.cdr.markForCheck();
            });
        },
        error: () => {
          this.updateDetailTableState(target, {
            loading: false,
            errorMessage: 'Unable to load popup table rows.',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private updateDetailTableState(target: 'popup' | 'inline', patch: Partial<SmartTableViewState>): void {
    if (target === 'popup') {
      this.popupTable = {
        ...this.popupTable,
        ...patch,
      };
      return;
    }

    this.inlineDetailTable = {
      ...this.inlineDetailTable,
      ...patch,
    };
  }

  closePopup(): void {
    this.popupOpen = false;
    this.popupTable = this.createEmptyTableState('popup');
    this.popupContextRow = null;
    this.popupContextColumn = undefined;
    this.cdr.markForCheck();
  }

  submitTable(): void {
    const submitAction = this.resolveSubmitAction('main');
    if (!this.userInputEnabled || !submitAction?.apiUrl || this.submitInFlight) {
      this.debug('submitTable skipped', {
        userInputEnabled: this.userInputEnabled,
        hasApiUrl: !!submitAction?.apiUrl,
        submitInFlight: this.submitInFlight,
      });
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, {}) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const requestPayload = this.resolveDynamicValue(submitAction.requestPayload, {});
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, {});
    const resolvedSubmitAction = {
      ...submitAction,
      customPayload: this.resolveDynamicValue(submitAction.customPayload, {}),
    };
    const filters = this.buildRequestFilters(
      requestPayload ?? {
        editedCellCount: this.editedCellCount,
        rows: this.cloneObject(this.rows),
      },
      queryParams,
    );

    this.debug('submitTable request', {
      widgetId: this.widgetConfig?.widgetId,
      apiUrl,
      method,
      filters: this.summarizeFilters(filters),
      requestPayload,
    });

    this.submitInFlight = true;
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.cdr.markForCheck();
    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
      this.autoSubmitTimer = undefined;
    }
    this.pendingAutoSubmit = false;

    const pendingEditActions = [
      ...this.takePendingColumnEditActions('main'),
      ...this.takePendingColumnEditActions('inline'),
    ];
    const pendingMainSubmitRows = this.takePendingSubmitRowEdits('main');
    const pendingInlineAutoSubmitRows = this.takePendingAutoSubmitEdits('inline').map((edit) => edit.row);
    const shouldRunDefaultMainSubmit =
      pendingMainSubmitRows.length === 0 && pendingInlineAutoSubmitRows.length === 0;

    const request$ = this.executePendingColumnEditActions$(pendingEditActions).pipe(
      switchMap(() => this.executePendingSubmitRowEdits$(submitAction, pendingMainSubmitRows)),
      switchMap(() => this.executePendingInlineAutoSubmitRows$(pendingInlineAutoSubmitRows)),
      switchMap(() =>
        shouldRunDefaultMainSubmit ? this.configService.getConfig(apiUrl, resolvedSubmitAction, filters, method) : of({}),
      ),
    );

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.debug('submitTable success', {
            widgetId: this.widgetConfig?.widgetId,
            responseKeys: Object.keys(response || {}),
          });
          const responseMessagePath = String(
            this.resolveDynamicValue(submitAction.responseMessagePath, {}) || '',
          );
          const messageFromResponse = responseMessagePath ? this.getValueByPath(response, responseMessagePath) : '';
          this.submitInFlight = false;
          this.submitMessage = String(messageFromResponse || submitAction.successMessage || 'Submitted successfully.');
          this.submitErrorMessage = '';
          if (submitAction.navigateAction) {
            executeNavigationFromConfig(
              this.router,
              { navigateAction: submitAction.navigateAction, widgetId: this.widgetConfig?.widgetId },
              { name: 'submit', value: 'submit' },
              { requestPayload, filters },
            );
          }
          this.closePendingColumnEditStates(pendingEditActions);
          this.closePendingSubmitRowEditStates(pendingMainSubmitRows);
          this.executePostSubmitActions(
            submitAction,
            pendingMainSubmitRows.length > 0
              ? pendingMainSubmitRows.map((pendingRow) => pendingRow.row)
              : [this.lastEditedRow],
          );
          this.refreshSubmitListenersWithoutPayload(submitAction);
          this.flushAutoSubmit();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.debug('submitTable error', {
            widgetId: this.widgetConfig?.widgetId,
            error,
          });
          this.submitInFlight = false;
          this.submitMessage = '';
          this.submitErrorMessage = submitAction.errorMessage || 'Submit failed.';
          this.flushAutoSubmit();
          this.cdr.markForCheck();
        },
      });
  }

  getSliderMin(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.minKey, column.min, 0);
  }

  getSliderMax(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.maxKey, column.max, 10);
  }

  getSliderStep(row: any, column: SmartTableColumn): number {
    return this.resolveNumericSetting(row, column.stepKey, column.step, this.getDefaultSliderStep(column));
  }

  getSliderPercent(row: any, column: SmartTableColumn): number {
    const value = this.toFiniteNumber(this.getValueByPath(row, column.key));
    const min = this.getSliderMin(row, column);
    const max = this.getSliderMax(row, column);
    const range = max - min;

    if (!Number.isFinite(value) || !Number.isFinite(range) || range <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, ((value - min) / range) * 100));
  }

  getProgressPercent(row: any, column: SmartTableColumn): number {
    const value = Number(this.resolveProgressValue(row, column));
    const max = Number(this.resolveProgressMax(row, column));
    if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  getProgressLabel(row: any, column: SmartTableColumn): string {
    const value = Number(this.resolveProgressValue(row, column));
    if (!Number.isFinite(value)) {
      return '0%';
    }
    return `${this.formatNumericValue(value, { ...column, type: 'number' }, undefined)}`;
  }

  getProgressColor(row: any, column: SmartTableColumn): string {
    const colorByKey = column.progressColorKey ? this.getValueByPath(row, column.progressColorKey) : '';
    return String(colorByKey || column.progressColor || '');
  }

  getStatusColor(row: any, column: SmartTableColumn): string {
    const keyColor = column.statusColorKey ? this.getValueByPath(row, column.statusColorKey) : '';
    return String(keyColor || column.statusColor || '');
  }

  getStatusLabel(row: any, column: SmartTableColumn): string {
    const value = this.getValueByPath(row, column.key);
    return value === undefined || value === null || value === '' ? 'Pending' : String(value);
  }

  private runRecalculationRequest() {
    const recalculation = this.widgetConfig?.recalculation;
    const apiUrl = recalculation?.apiUrl;
    if (!apiUrl || !this.widgetConfig) {
      return of<RecalculationRules | null>(null);
    }

    const method = this.resolveMethod(apiUrl, recalculation.method);

    this.apiSyncing = true;
    this.syncMessage = 'Syncing derived columns...';
    this.updateSyncStateColumns('Syncing...', this.getConfiguredSyncStateColor('pending'));
    this.cdr.markForCheck();

    const requestPayload = {
      editedCellCount: this.editedCellCount,
      rows: this.cloneObject(this.rows),
    };

    return this.configService
      .getConfig(apiUrl, recalculation, { requestParams: requestPayload }, method)
      .pipe(
        catchError(() => {
          this.apiSyncing = false;
          this.syncMessage = 'Sync failed. JSON rule set could not be loaded.';
          this.updateSyncStateColumns('Sync failed', this.getConfiguredSyncStateColor('error'));
          this.cdr.markForCheck();
          return of<RecalculationRules | null>(null);
        }),
      );
  }

  private shouldRunRecalculation(): boolean {
    const recalculation = this.widgetConfig?.recalculation;
    return !!recalculation?.apiUrl && recalculation.enabled !== false && this.editedCellCount >= this.editTriggerCount;
  }

  private applyRecalculationRules(response: any): void {
    const recalculation = this.widgetConfig?.recalculation;
    if (!recalculation) {
      return;
    }

    const rules = this.extractRecalculationRules(response, recalculation.responsePath);
    const weights = rules.weights || {};
    const formula = this.resolveFormulaKeys(recalculation);
    const outputs = this.resolveOutputKeys(recalculation);
    const scoreKey = outputs.score || 'healthScore';
    const scoreColorKey = outputs.scoreColor || 'healthColor';
    const projectionKey = outputs.projection || 'projectedRevenue';
    const statusKey = outputs.status || 'autoStatus';
    const statusColorKey = outputs.statusColor || 'autoStatusColor';
    const syncMessage = rules.syncMessage || `Synced after ${this.editedCellCount} edits`;

    this.rows = this.rows.map((row) => {
      const confidence = this.toFiniteNumber(row[formula.confidenceKey || 'confidence']);
      const priceLift = this.toFiniteNumber(row[formula.priceLiftKey || 'priceLift']);
      const risk = this.toFiniteNumber(row[formula.riskKey || 'risk']);
      const resourceLoad = this.toFiniteNumber(row[formula.resourceLoadKey || 'resourceLoad']);
      const baseValue = this.toFiniteNumber(row[formula.baseValueKey || 'baseRevenue']);

      const score =
        25
        + (confidence * this.toFiniteNumber(weights['confidence']))
        + (priceLift * this.toFiniteNumber(weights['priceLift']))
        - (risk * this.toFiniteNumber(weights['riskPenalty']))
        - (resourceLoad * this.toFiniteNumber(weights['resourceLoadPenalty']))
        + (this.editedCellCount * this.toFiniteNumber(weights['editedCellBonus']));

      const boundedScore = Math.max(0, Math.min(100, Math.round(score)));
      const statusBand = this.resolveStatusBand(boundedScore, rules.statusBands || []);
      const projectedRevenue =
        (baseValue * (1 + priceLift / 100))
        + (confidence * this.toFiniteNumber(rules.projectionConfidenceBonus))
        - (risk * this.toFiniteNumber(rules.projectionRiskPenalty))
        - (resourceLoad * this.toFiniteNumber(rules.projectionLoadPenalty));

      return {
        ...row,
        [scoreKey]: boundedScore,
        [scoreColorKey]: statusBand.color,
        [projectionKey]: this.roundValue(projectedRevenue, 2),
        [statusKey]: statusBand.label,
        [statusColorKey]: statusBand.color,
      };
    });

    this.apiSyncing = false;
    this.syncMessage = syncMessage;
    this.updateSyncStateColumns(syncMessage, this.getConfiguredSyncStateColor('success'));
    this.cdr.markForCheck();
  }

  private updateSyncStateColumns(message: string, color: string): void {
    const outputs = this.resolveOutputKeys(this.widgetConfig?.recalculation);
    const syncKey = outputs.syncState || 'syncState';
    const syncColorKey = outputs.syncColor || 'syncColor';
    this.rows = this.rows.map((row) => ({
      ...row,
      [syncKey]: message,
      ...(color ? { [syncColorKey]: color } : {}),
    }));
  }

  private extractRecalculationRules(response: any, responsePath?: string): RecalculationRules {
    const fromPath = responsePath ? this.getValueByPath(response, responsePath) : undefined;
    const candidate = fromPath ?? response;
    return candidate && typeof candidate === 'object' ? candidate as RecalculationRules : {};
  }

  private resolveStatusBand(score: number, bands: RecalculationBand[]): { label: string; color: string } {
    const sortedBands = [...bands].sort((left, right) => this.toFiniteNumber(right.min) - this.toFiniteNumber(left.min));
    const band = sortedBands.find((item) => score >= this.toFiniteNumber(item.min));
    return {
      label: band?.label || 'Pending',
      color: band?.color || '',
    };
  }

  private getConfiguredSyncStateColor(state: 'pending' | 'success' | 'error'): string {
    return String(this.widgetConfig?.recalculation?.syncStateColors?.[state] || '');
  }

  private registerEdit(scope: SmartTableScope, rowIndex: number, row: any, column: SmartTableColumn, event?: any): void {
    const editAction = column.editAction;
    if (
      this.inlineDetailRowId &&
      (editAction?.refreshOnSubmitSuccess === true || (editAction as any)?.refreshDashboardOnSuccess === true)
    ) {
      this.pendingInlineDetailScrollRestore = true;
    }

    this.lastEditedRow = this.cloneObject(row);
    this.executeColumnEditAction(column, row, event);
    const shouldTrackRecalculation = this.shouldTrackColumnForRecalculation(column);
    this.debug('registerEdit', {
      widgetId: this.widgetConfig?.widgetId,
      rowIndex,
      column: column.bindingKey || column.key,
      value: row?.[column.key],
      shouldTrackRecalculation,
      shouldAutoSubmit: this.shouldAutoSubmitColumn(column, scope),
      editedRow: this.lastEditedRow,
    });

    if (scope === 'main' && shouldTrackRecalculation) {
      const trackedKey = column.bindingKey || column.key;
      const editKey = `${this.getRowId(row, rowIndex)}::${trackedKey}`;
      this.editedCellKeys.add(editKey);
      this.syncMessage = this.getPendingSyncMessage();
      this.recalculationTrigger$.next();
    }

    if (this.shouldAutoSubmitColumn(column, scope)) {
      if (scope !== 'main') {
        this.queuePendingAutoSubmitEdit(scope, rowIndex, row, column);
      }
      this.scheduleAutoSubmit(scope);
    }

    this.cdr.markForCheck();
  }

  private executeColumnEditAction(column: SmartTableColumn, row?: any, event?: any): void {
    const editAction = column.editAction;
    if (!editAction?.apiUrl) {
      return;
    }

    this.buildColumnEditActionRequest$(column, row, event)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.applyEditActionSuccessEffects(editAction);
        },
        error: (error) => this.debug('column edit action failed', {
          widgetId: this.widgetConfig?.widgetId,
          column: column.bindingKey || column.key,
          error,
        }),
      });
  }

  private buildColumnEditActionRequest$(column: SmartTableColumn, row?: any, event?: any): Observable<any> {
    const editAction = column.editAction;
    if (!editAction?.apiUrl) {
      return of(null);
    }

    const context = { row: row || this.lastEditedRow, column, event };
    const apiUrl = String(this.resolveDynamicValue(editAction.apiUrl, context) || '');
    const method = this.resolveMethod(apiUrl, editAction.method);
    const requestPayload = this.resolveDynamicValue(editAction.requestPayload, context);
    const queryParams = this.resolveDynamicValue(editAction.queryParams, context);
    const resolvedEditAction = {
      ...editAction,
      customPayload: this.resolveDynamicValue(editAction.customPayload, context),
    };

    return this.configService.getConfig(
      apiUrl,
      resolvedEditAction,
      this.buildRequestFilters(requestPayload, queryParams),
      method,
    );
  }

  private executePendingColumnEditActions$(pendingActions: PendingColumnEditAction[]): Observable<any> {
    let request$: Observable<any> = of(null);

    pendingActions.forEach((pendingAction) => {
      request$ = request$.pipe(
        switchMap(() =>
          this.buildColumnEditActionRequest$(pendingAction.column, pendingAction.row, pendingAction.event).pipe(
            map((response) => {
              this.applyEditActionSuccessEffects(pendingAction.column.editAction);
              return response;
            }),
          ),
        ),
      );
    });

    return request$;
  }

  private buildMainSubmitRowRequest$(submitAction: SmartTableSubmitActionConfig, editedRow: any): Observable<any> {
    const context = { editedRow, row: editedRow };
    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, context) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const requestPayload = this.resolveDynamicValue(submitAction.requestPayload, context);
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, context);
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const resolvedSubmitAction = {
      ...submitAction,
      customPayload: this.resolveDynamicValue(submitAction.customPayload, context),
    };

    return this.configService.getConfig(apiUrl, resolvedSubmitAction, filters, method);
  }

  private executePostSubmitActions(submitAction: SmartTableSubmitActionConfig, editedRows: any[]): void {
    const actions = submitAction.postSubmitActions || [];
    editedRows.filter(Boolean).forEach((editedRow) => {
      actions.forEach((action) => {
        if (!action?.apiUrl) {
          return;
        }
        this.buildMainSubmitRowRequest$(action, editedRow)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            error: (error) => this.debug('post submit action failed', {
              widgetId: this.widgetConfig?.widgetId,
              apiUrl: action.apiUrl,
              error,
            }),
          });
      });
    });
  }

  private executePendingSubmitRowEdits$(
    submitAction: SmartTableSubmitActionConfig,
    pendingRows: PendingSubmitRowEdit[],
  ): Observable<any> {
    let request$: Observable<any> = of(null);

    pendingRows.forEach((pendingRow) => {
      request$ = request$.pipe(
        switchMap(() => this.buildMainSubmitRowRequest$(submitAction, pendingRow.row)),
      );
    });

    return request$;
  }

  private executePendingInlineAutoSubmitRows$(pendingRows: any[]): Observable<any> {
    if (pendingRows.length === 0) {
      return of(null);
    }

    const submitAction = this.resolveSubmitAction('inline');
    if (!submitAction?.apiUrl) {
      return of(null);
    }

    const request$ = pendingRows.length > 1
      ? this.buildMultiAutoSubmitRequest$(submitAction, pendingRows, 'inline')
      : this.buildAutoSubmitRequest$(submitAction, pendingRows[0], 'inline');

    return request$.pipe(
      map((response) => {
        this.applySubmitActionSuccessEffects(submitAction, 'inline');
        return response;
      }),
    );
  }

  private shouldAutoSubmitColumn(column: SmartTableColumn, scope: SmartTableScope = 'main'): boolean {
    if (!this.userInputEnabled) {
      return false;
    }

    const submitAction = this.resolveSubmitAction(scope);
    if (!submitAction?.apiUrl || submitAction.autoSubmitOnEdit !== true) {
      this.debug('shouldAutoSubmitColumn blocked by submitAction', {
        widgetId: this.widgetConfig?.widgetId,
        column: column.bindingKey || column.key,
        hasSubmitAction: !!submitAction,
        apiUrl: submitAction?.apiUrl,
        autoSubmitOnEdit: submitAction?.autoSubmitOnEdit,
      });
      return false;
    }

    const configuredColumns = Array.isArray(submitAction.triggerColumns)
      ? submitAction.triggerColumns.map((value) => String(value || '').trim()).filter((value) => !!value)
      : [];

    if (configuredColumns.length === 0) {
      this.debug('shouldAutoSubmitColumn default-mode', {
        widgetId: this.widgetConfig?.widgetId,
        column: column.bindingKey || column.key,
        isEditable: this.isEditableColumn(column),
        isDropdown: this.isDropdownColumn(column),
      });
      return this.isEditableColumn(column) || this.isDropdownColumn(column);
    }

    const candidates = [column.bindingKey, column.key]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value);
    const matched = candidates.some((candidate) => configuredColumns.includes(candidate));
    this.debug('shouldAutoSubmitColumn trigger-match', {
      widgetId: this.widgetConfig?.widgetId,
      column: column.bindingKey || column.key,
      candidates,
      configuredColumns,
      matched,
    });
    return matched;
  }

  private shouldCommitInputOnBlur(scope: SmartTableScope, column: SmartTableColumn): boolean {
    if (typeof (column as any)?.commitOnBlur === 'boolean') {
      return (column as any).commitOnBlur;
    }

    const submitAction = this.resolveSubmitAction(scope);
    if (typeof submitAction?.commitOnBlur === 'boolean') {
      return submitAction.commitOnBlur;
    }

    return true;
  }

  private buildEditableCellKey(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): string {
    return `${scope}::${this.getRowId(row, rowIndex)}::${column.bindingKey || column.key}`;
  }

  private buildEditableColumnKey(scope: SmartTableScope, column: SmartTableColumn): string {
    return `${scope}::${column.bindingKey || column.key}`;
  }

  private scheduleAutoSubmit(scope: SmartTableScope = 'main'): void {
    this.pendingAutoSubmit = true;
    this.debug('scheduleAutoSubmit', {
      widgetId: this.widgetConfig?.widgetId,
      delayMs: this.getAutoSubmitDelayMs(scope),
      editedRow: this.lastEditedRow,
      scope,
    });

    if (this.autoSubmitTimer) {
      clearTimeout(this.autoSubmitTimer);
    }

    this.autoSubmitTimer = setTimeout(() => {
      this.autoSubmitTimer = undefined;
      this.flushAutoSubmit(scope);
    }, this.getAutoSubmitDelayMs(scope));
  }

  private flushAutoSubmit(scope: SmartTableScope = 'main'): void {
    if (!this.userInputEnabled) {
      this.pendingAutoSubmit = false;
      return;
    }

    this.debug('flushAutoSubmit start', {
      widgetId: this.widgetConfig?.widgetId,
      pendingAutoSubmit: this.pendingAutoSubmit,
      submitInFlight: this.submitInFlight,
      editedRow: this.lastEditedRow,
      scope,
    });
    if (!this.pendingAutoSubmit || this.submitInFlight) {
      return;
    }

    this.pendingAutoSubmit = false;
    this.syncEditedRowToGlobalFilters(scope);
    if (scope === 'main') {
      this.submitTable();
      return;
    }

    const submitAction = this.resolveSubmitAction(scope);
    if (!submitAction?.apiUrl) {
      return;
    }

    const queuedEdits = this.takePendingAutoSubmitEdits(scope);
    const request$ = queuedEdits.length > 1
      ? this.buildMultiAutoSubmitRequest$(submitAction, queuedEdits, scope)
      : this.buildAutoSubmitRequest$(submitAction, queuedEdits[0]?.row ?? this.lastEditedRow, scope, queuedEdits[0]?.column);

    this.submitInFlight = true;
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.cdr.markForCheck();

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submitInFlight = false;
          this.submitMessage = String(submitAction.successMessage || 'Submitted successfully.');
          this.submitErrorMessage = '';
          this.refreshSubmitListenersWithoutPayload(submitAction);
          this.flushAutoSubmit(scope);
          // The pencil toggle is a deliberate opt-in to editing: once the change is
          // saved, drop back to read-only so values can't be altered by a stray click.
          // Re-clicking the pencil starts a new edit session.
          if (scope === 'inline' && !this.inlineDetailHideEditToggle) {
            this.inlineDetailEditModeEnabled = false;
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.debug('submitTable error', {
            widgetId: this.widgetConfig?.widgetId,
            error,
            scope,
          });
          this.submitInFlight = false;
          this.submitMessage = '';
          this.submitErrorMessage = submitAction.errorMessage || 'Submit failed.';
          this.flushAutoSubmit(scope);
          this.cdr.markForCheck();
        },
      });
  }

  private buildAutoSubmitRequest$(
    submitAction: SmartTableSubmitActionConfig,
    editedRow: any,
    scope: SmartTableScope,
    column?: SmartTableColumn,
  ): Observable<any> {
    const columnKey = column?.bindingKey || column?.key || '';
    const context = {
      editedRow,
      row: scope === 'popup' ? this.popupContextRow : this.inlineContextRow,
      column,
      editedValue: columnKey ? this.getValueByPath(editedRow, columnKey) : undefined,
    };
    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, context) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const requestPayload = this.resolveDynamicValue(submitAction.requestPayload, context);
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, context);
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const resolvedSubmitAction = {
      ...submitAction,
      customPayload: this.resolveDynamicValue(submitAction.customPayload, context),
    };

    return this.configService.getConfig(apiUrl, resolvedSubmitAction, filters, method);
  }

  private buildMultiAutoSubmitRequest$(
    submitAction: SmartTableSubmitActionConfig,
    editedRows: Array<any | PendingAutoSubmitEdit>,
    scope: SmartTableScope,
  ): Observable<any> {
    let request$: Observable<any> = of(null);

    editedRows.forEach((editedRowOrEdit) => {
      const pendingEdit = this.isPendingAutoSubmitEdit(editedRowOrEdit) ? editedRowOrEdit : null;
      const editedRow = pendingEdit?.row ?? editedRowOrEdit;
      request$ = request$.pipe(
        switchMap(() => this.buildAutoSubmitRequest$(submitAction, editedRow, scope, pendingEdit?.column)),
      );
    });

    return request$;
  }

  private isPendingAutoSubmitEdit(value: any): value is PendingAutoSubmitEdit {
    return !!value && typeof value === 'object' && 'row' in value && 'column' in value && 'scope' in value;
  }

  private buildBatchAutoSubmitRequest$(
    submitAction: SmartTableSubmitActionConfig,
    editedRows: any[],
    scope: SmartTableScope,
  ): Observable<any> {
    const baseContext: SmartTableTemplateContext = {
      row: scope === 'popup' ? this.popupContextRow : this.inlineContextRow,
      editedRows,
      editedRow: editedRows[editedRows.length - 1],
    };
    const perRowPayloads = editedRows.map((editedRow) =>
      this.resolveDynamicValue(submitAction.requestPayload, { ...baseContext, editedRow }),
    );
    const sharedOperation = perRowPayloads.length > 0 && perRowPayloads.every(
      (payload) =>
        payload &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        payload.operation === perRowPayloads[0]?.operation,
    )
      ? perRowPayloads[0]?.operation
      : undefined;
    const requestPayload = {
      ...(sharedOperation !== undefined ? { operation: sharedOperation } : {}),
      rows: perRowPayloads.map((payload) => {
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
          return payload;
        }

        if (sharedOperation === undefined) {
          return payload;
        }

        const { operation, ...rest } = payload;
        return rest;
      }),
    };
    const apiUrl = String(this.resolveDynamicValue(submitAction.apiUrl, baseContext) || '');
    const method = this.resolveMethod(apiUrl, submitAction.method);
    const queryParams = this.resolveDynamicValue(submitAction.queryParams, baseContext);
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const resolvedSubmitAction = {
      ...submitAction,
      customPayload: this.resolveDynamicValue(submitAction.customPayload, baseContext),
    };

    return this.configService.getConfig(apiUrl, resolvedSubmitAction, filters, method);
  }

  private queuePendingAutoSubmitEdit(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): void {
    this.pendingAutoSubmitEdits.set(
      this.buildPendingAutoSubmitRowKey(scope, rowIndex, row),
      {
        scope,
        rowIndex,
        row: this.cloneObject(row),
        column,
      },
    );
  }

  private queuePendingColumnEditAction(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
    event?: any,
  ): void {
    this.pendingColumnEditActions.set(
      this.buildPendingAutoSubmitEditKey(scope, rowIndex, row, column),
      {
        scope,
        rowIndex,
        row: this.cloneObject(row),
        column,
        event,
      },
    );
  }

  private queuePendingSubmitRowEdit(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): void {
    this.pendingSubmitRowEdits.set(
      `${scope}::${this.getRowId(row, rowIndex)}`,
      {
        scope,
        rowIndex,
        row: this.cloneObject(row),
        column,
      },
    );
  }

  private clearPendingColumnEditAction(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): void {
    this.pendingColumnEditActions.delete(
      this.buildPendingAutoSubmitEditKey(scope, rowIndex, row, column),
    );
  }

  private clearPendingSubmitRowEdit(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    _column: SmartTableColumn,
  ): void {
    this.pendingSubmitRowEdits.delete(`${scope}::${this.getRowId(row, rowIndex)}`);
  }

  private takePendingColumnEditActions(scope: SmartTableScope): PendingColumnEditAction[] {
    const actions: PendingColumnEditAction[] = [];
    const scopePrefix = `${scope}::`;
    Array.from(this.pendingColumnEditActions.entries()).forEach(([key, action]) => {
      if (!key.startsWith(scopePrefix)) {
        return;
      }

      actions.push(action);
      this.pendingColumnEditActions.delete(key);
    });

    return actions;
  }

  private takePendingSubmitRowEdits(scope: SmartTableScope): PendingSubmitRowEdit[] {
    const edits: PendingSubmitRowEdit[] = [];
    const scopePrefix = `${scope}::`;
    Array.from(this.pendingSubmitRowEdits.entries()).forEach(([key, edit]) => {
      if (!key.startsWith(scopePrefix)) {
        return;
      }

      edits.push(edit);
      this.pendingSubmitRowEdits.delete(key);
    });

    return edits;
  }

  private shouldQueueMainSubmitRow(column: SmartTableColumn): boolean {
    if (column.editAction?.apiUrl) {
      return false;
    }

    if (!this.isEditableColumn(column, 'main')) {
      return false;
    }

    const submitAction = this.resolveSubmitAction('main');
    return !!submitAction?.apiUrl;
  }

  private closePendingColumnEditStates(pendingActions: PendingColumnEditAction[]): void {
    pendingActions.forEach((pendingAction) => {
      if (pendingAction.column.showInlineEditToggle === true) {
        this.activeInlineEditColumnKey = null;
      }

      if (this.isClickToEditColumn(pendingAction.column)) {
        this.activeEditableColumnKeys.delete(
          this.buildEditableColumnKey(pendingAction.scope, pendingAction.column),
        );
        this.autoOpenEmptyCellKeys.delete(
          this.buildEditableCellKey(
            pendingAction.scope,
            pendingAction.rowIndex,
            pendingAction.row,
            pendingAction.column,
          ),
        );
      }
    });

    this.activeEditableCellKey = null;
  }

  private closePendingSubmitRowEditStates(pendingRows: PendingSubmitRowEdit[]): void {
    pendingRows.forEach((pendingRow) => {
      if (pendingRow.column.showInlineEditToggle === true) {
        this.activeInlineEditColumnKey = null;
      }

      if (this.isClickToEditColumn(pendingRow.column)) {
        this.activeEditableColumnKeys.delete(
          this.buildEditableColumnKey(pendingRow.scope, pendingRow.column),
        );
        this.autoOpenEmptyCellKeys.delete(
          this.buildEditableCellKey(
            pendingRow.scope,
            pendingRow.rowIndex,
            pendingRow.row,
            pendingRow.column,
          ),
        );
      }
    });

    this.activeEditableCellKey = null;
  }

  private applyEditActionSuccessEffects(editAction?: SmartTableSubmitActionConfig): void {
    if ((editAction as any)?.refreshDashboardOnSuccess === true || editAction?.refreshOnSubmitSuccess === true) {
      this.globalFilterService.emitRefreshWithoutPayload(
        editAction?.refreshFilterListener || editAction?.filterListener,
        null,
      );
    }
  }

  private applySubmitActionSuccessEffects(
    submitAction: SmartTableSubmitActionConfig,
    scope: SmartTableScope,
  ): void {
    if (submitAction.clearFiltersOnSubmitSuccess === true) {
      this.globalFilterService.clearAllFilters();
    }
    this.refreshSubmitListenersWithoutPayload(submitAction);
    if (scope === 'inline') {
      this.inlineDetailEditModeEnabled = false;
      this.activeInlineEditColumnKey = null;
      this.activeEditableCellKey = null;
    }
  }

  private takePendingAutoSubmitEdits(scope: SmartTableScope): PendingAutoSubmitEdit[] {
    const edits: PendingAutoSubmitEdit[] = [];
    const scopePrefix = `${scope}::`;
    Array.from(this.pendingAutoSubmitEdits.entries()).forEach(([key, edit]) => {
      if (!key.startsWith(scopePrefix)) {
        return;
      }
      edits.push(edit);
      this.pendingAutoSubmitEdits.delete(key);
    });
    return edits;
  }

  private buildPendingAutoSubmitEditKey(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
    column: SmartTableColumn,
  ): string {
    return `${scope}::${this.getRowId(row, rowIndex)}::${column.bindingKey || column.key}`;
  }

  private buildPendingAutoSubmitRowKey(
    scope: SmartTableScope,
    rowIndex: number,
    row: any,
  ): string {
    return `${scope}::${this.getRowId(row, rowIndex)}`;
  }

  private refreshSubmitListenersWithoutPayload(submitAction: SmartTableSubmitActionConfig): void {
    if (submitAction.refreshOnSubmitSuccess !== true) {
      return;
    }

    this.clearColumnValueApiCache();

    const listener =
      submitAction.refreshFilterListener ||
      submitAction.filterListener ||
      this.widgetConfig?.filterListener ||
      this.widgetConfig?.filterListner ||
      this.widgetConfig?.widgetId;

    this.debug('refreshSubmitListenersWithoutPayload emit', {
      widgetId: this.widgetConfig?.widgetId,
      listener,
    });

    this.globalFilterService.emitRefreshWithoutPayload(listener, this.widgetConfig?.widgetId || null);
  }

  private clearColumnValueApiCache(): void {
    if (this.columnValueApiCache.size === 0) {
      return;
    }

    this.columnValueApiCache.clear();
  }

  private getAutoSubmitDelayMs(scope: SmartTableScope = 'main'): number {
    const raw = Number(this.resolveSubmitAction(scope)?.debounceMs);
    return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 500;
  }

  private syncEditedRowToGlobalFilters(scope: SmartTableScope = 'main'): void {
    const submitAction = this.resolveSubmitAction(scope);
    const editedRow = this.lastEditedRow;
    if (!submitAction?.syncFiltersOnEdit || !editedRow) {
      this.debug('syncEditedRowToGlobalFilters skipped', {
        widgetId: this.widgetConfig?.widgetId,
        syncFiltersOnEdit: submitAction?.syncFiltersOnEdit,
        hasEditedRow: !!editedRow,
      });
      return;
    }

    const filterKeys = Array.isArray(submitAction.filterKeys) && submitAction.filterKeys.length > 0
      ? submitAction.filterKeys
      : (Array.isArray(submitAction.triggerColumns) ? submitAction.triggerColumns : []);
    if (filterKeys.length === 0) {
      return;
    }

    const listener = submitAction.filterListener || this.widgetConfig?.filterListener || this.widgetConfig?.filterListner;
    const normalizedKeys = filterKeys
      .map((key) => String(key || '').trim())
      .filter((key) => !!key && this.getValueByPath(editedRow, key) !== undefined);

    this.debug('syncEditedRowToGlobalFilters emit', {
      widgetId: this.widgetConfig?.widgetId,
      listener,
      normalizedKeys,
      values: normalizedKeys.reduce((acc: Record<string, any>, key) => {
        acc[key] = this.getValueByPath(editedRow, key);
        return acc;
      }, {}),
    });

    normalizedKeys.forEach((key, index) => {
      const isLast = index === normalizedKeys.length - 1;
      this.globalFilterService.updateFilterSelection(
        key,
        this.getValueByPath(editedRow, key),
        false,
        this.widgetConfig?.widgetId,
        isLast,
        listener,
        undefined,
        true,
      );
    });
  }

  private resolveSubmitAction(scope: SmartTableScope = 'main'): SmartTableSubmitActionConfig | undefined {
    if (scope === 'popup') {
      return this.popupTable.submitAction || this.widgetConfig?.submitAction;
    }

    if (scope === 'inline') {
      return this.inlineDetailTable.submitAction || this.widgetConfig?.submitAction;
    }

    return this.widgetConfig?.submitAction;
  }

  private shouldTrackColumnForRecalculation(column: SmartTableColumn): boolean {
    const trackedColumns = this.getTriggerColumnKeys();
    if (trackedColumns.length === 0) {
      return this.isEditableColumn(column) || this.isDropdownColumn(column);
    }

    const candidates = [column.bindingKey, column.key]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value);

    return candidates.some((candidate) => trackedColumns.includes(candidate));
  }

  private getTriggerColumnKeys(): string[] {
    const recalculation = this.widgetConfig?.recalculation;
    const triggerConfig = recalculation?.triggerAfterChanges;
    if (triggerConfig) {
      if (typeof triggerConfig === 'string') {
        return [triggerConfig.trim()].filter((value) => !!value);
      }

      if (Array.isArray(triggerConfig)) {
        return triggerConfig.map((value) => String(value || '').trim()).filter((value) => !!value);
      }

      if (typeof triggerConfig === 'object') {
        const raw =
          triggerConfig.columnKeys ||
          triggerConfig.keys ||
          triggerConfig.columns ||
          triggerConfig.bindingKeys ||
          [];

        return Array.isArray(raw)
          ? raw.map((value) => String(value || '').trim()).filter((value) => !!value)
          : [];
      }
    }

    return Array.isArray(recalculation?.watchColumns)
      ? recalculation!.watchColumns!.map((value) => String(value || '').trim()).filter((value) => !!value)
      : [];
  }

  private getPendingSyncMessage(): string {
    if (this.editedCellCount >= this.editTriggerCount) {
      return this.apiSyncing ? 'Syncing derived columns...' : 'Edit threshold met. Derived columns will sync automatically.';
    }

    const remaining = this.editTriggerCount - this.editedCellCount;
    return `${remaining} more editable ${remaining === 1 ? 'change' : 'changes'} needed before recalculation.`;
  }

  private normalizeColumns(config: Pick<SmartTableConfig | SmartTablePopupConfig, 'columns' | 'columnBindings'>): SmartTableColumn[] {
    const inputColumns = Array.isArray(this.inputData?.columns) && config === this.widgetConfig ? this.inputData!.columns! : [];
    const configColumns = Array.isArray(config.columns) ? config.columns : [];
    const sourceColumns = inputColumns.length > 0 ? inputColumns : configColumns;
    if (sourceColumns.length === 0) {
      return [];
    }

    const bindings = config.columnBindings || {};
    return sourceColumns.map((column) => this.resolveColumnBinding(column, bindings));
  }

  private resolveColumnBinding(
    column: SmartTableColumn,
    bindings: Record<string, Partial<SmartTableColumn>>,
  ): SmartTableColumn {
    const bindingKey = column.bindingKey || column.key;
    const binding = bindingKey ? bindings[bindingKey] : undefined;
    if (!binding) {
      return column;
    }

    const merged = {
      ...binding,
      ...column,
      key: column.key || binding.key || '',
      bindingKey,
    } as SmartTableColumn;

    if (!merged.key) {
      merged.key = bindingKey;
    }

    return merged;
  }

  private extractRowsFromSource(source: any, config: Pick<SmartTableConfig | SmartTablePopupConfig, 'rowsPath' | 'dataKey'>): any[] {
    if (Array.isArray(source)) {
      return this.cloneObject(source);
    }

    const candidatePaths = [config.rowsPath, config.dataKey].filter(Boolean) as string[];
    for (const path of candidatePaths) {
      const value = this.getValueByPath(source, path);
      if (Array.isArray(value)) {
        return this.cloneObject(value);
      }
      if (value && typeof value === 'object') {
        return [this.cloneObject(value)];
      }
    }

    const directRows = source?.tableRows || source?.rows || source?.data || source?.tableData;
    if (Array.isArray(directRows)) {
      return this.cloneObject(directRows);
    }

    if (directRows && typeof directRows === 'object') {
      return [this.cloneObject(directRows)];
    }

    if (source && typeof source === 'object') {
      return [this.cloneObject(source)];
    }

    return [];
  }

  private scheduleInlineDetailScroll(): void {
    setTimeout(() => {
      const activeInlineDetail = this.hostElement.nativeElement.querySelector('.inline-detail-row');
      if (activeInlineDetail instanceof HTMLElement) {
        activeInlineDetail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }

  private resolvePopupRowsForDisplay(rows: any[], popupConfig: SmartTablePopupConfig): Observable<any[]> {
    const normalizedRows = this.normalizePlanningStatusRows(rows);
    if (!popupConfig?.inheritApaPricingLever || !Array.isArray(normalizedRows) || normalizedRows.length === 0) {
      return of(this.sortRowsForDisplay(normalizedRows, popupConfig));
    }

    const apaRangesUrl = String(
      popupConfig.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
    ).trim();

    if (!apaRangesUrl) {
      return of(this.sortRowsForDisplay(normalizedRows, popupConfig));
    }

    return this.configService.getData(apaRangesUrl).pipe(
      map((response) => this.sortRowsForDisplay(this.applyApaRangesToPopupRows(normalizedRows, response, popupConfig), popupConfig)),
      catchError(() => of(this.sortRowsForDisplay(normalizedRows, popupConfig))),
    );
  }

  private resolveInitialRowsForDisplay(rows: any[], config: SmartTableConfig): Observable<any[]> {
    const normalizedRows = this.normalizePlanningStatusRows(rows);
    if (!config?.inheritApaPricingLever || !Array.isArray(normalizedRows) || normalizedRows.length === 0) {
      return of(normalizedRows);
    }

    const apaRangesUrl = String(
      config.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
    ).trim();

    if (!apaRangesUrl) {
      return of(normalizedRows);
    }

    return this.configService.getData(apaRangesUrl).pipe(
      map((response) => this.applyApaRangesToInitialRows(normalizedRows, response, config)),
      catchError(() => of(normalizedRows)),
    );
  }

  private applyApaRangesToPopupRows(rows: any[], source: any, popupConfig: SmartTablePopupConfig): any[] {
    const ranges = this.extractApaRanges(source, popupConfig);
    if (!ranges.length) {
      return rows;
    }

    const overrides = this.readApaOverridesMap();
    const labelKey = popupConfig.apaRangeLabelKey || 'label';
    const valueKey = popupConfig.apaRangeValueKey || 'currentValue';
    const productTypeKey = popupConfig.apaRangeProductTypeKey || 'product_price_type';
    const targetColumnKey = popupConfig.apaRangeTargetColumnKey || 'pricing_lever';
    const minKey = popupConfig.apaRangeMinKey || `${targetColumnKey}_min`;
    const maxKey = popupConfig.apaRangeMaxKey || `${targetColumnKey}_max`;
    const stepKey = popupConfig.apaRangeStepKey || `${targetColumnKey}_step`;

    return rows.map((row) => {
      const normalizedProductType = this.normalizeApaLabel(this.getValueByPath(row, productTypeKey));
      if (!normalizedProductType) {
        return row;
      }

      const matchedRange = ranges.find((range) => this.normalizeApaLabel(range?.[labelKey]) === normalizedProductType);
      if (!matchedRange) {
        return row;
      }

      const override = this.resolveStoredApaOverride(overrides[this.normalizeApaLabel(matchedRange?.[labelKey])]);
      const min = Number(override?.['min'] ?? matchedRange?.min);
      const max = Number(override?.['max'] ?? matchedRange?.max);
      const rowValue = this.getValueByPath(row, targetColumnKey);
      const hasRowValue = rowValue !== undefined && rowValue !== null && rowValue !== '' && Number.isFinite(Number(rowValue));
      const rawValue = hasRowValue
        ? rowValue
        : (override?.['value'] ?? matchedRange?.[valueKey] ?? matchedRange?.value);
      const nextValue = hasRowValue
        ? Number(rowValue)
        : this.clampPopupSliderValue(rawValue, min, max);

      return {
        ...row,
        [targetColumnKey]: nextValue,
        [minKey]: Number.isFinite(min) ? min : row?.[minKey],
        [maxKey]: Number.isFinite(max) ? max : row?.[maxKey],
        [stepKey]: this.resolveApaSliderStep(min, max),
      };
    });
  }

  private applyApaRangesToInitialRows(rows: any[], source: any, config: SmartTableConfig): any[] {
    const ranges = this.extractApaRanges(source, {
      apaRangeValueKey: config.apaRangeValueKey,
    } as SmartTablePopupConfig);
    if (!ranges.length) {
      return rows;
    }

    const overrides = this.readApaOverridesMap();
    const labelKey = config.apaRangeLabelKey || 'label';
    const valueKey = config.apaRangeValueKey || 'currentValue';
    const productTypeKey = config.apaRangeProductTypeKey || 'product_price_type';
    const targetColumnKey = config.apaRangeTargetColumnKey || 'pricing_lever';
    const minKey = config.apaRangeMinKey || `${targetColumnKey}_min`;
    const maxKey = config.apaRangeMaxKey || `${targetColumnKey}_max`;
    const stepKey = config.apaRangeStepKey || `${targetColumnKey}_step`;
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const incomingLeverRaw =
      currentFilters?.requestParams?.[targetColumnKey] ??
      currentFilters?.queryParams?.[targetColumnKey];
    const incomingLever = Array.isArray(incomingLeverRaw) ? incomingLeverRaw[0] : incomingLeverRaw;
    const hasIncomingLever = Number.isFinite(Number(incomingLever));

    return rows.map((row) => {
      const normalizedProductType = this.normalizeApaLabel(this.getValueByPath(row, productTypeKey));
      if (!normalizedProductType) {
        return row;
      }

      const matchedRange = ranges.find((range) => this.normalizeApaLabel(range?.[labelKey]) === normalizedProductType);
      if (!matchedRange) {
        return row;
      }

      const override = this.resolveStoredApaOverride(overrides[this.normalizeApaLabel(matchedRange?.[labelKey])]);
      const rawValue = hasIncomingLever
        ? incomingLever
        : (override?.['value'] ?? matchedRange?.[valueKey] ?? matchedRange?.value);
      const min = Number(override?.['min'] ?? matchedRange?.min);
      const max = Number(override?.['max'] ?? matchedRange?.max);
      const nextValue = this.clampPopupSliderValue(rawValue, min, max);

      return {
        ...row,
        [targetColumnKey]: nextValue,
        [minKey]: Number.isFinite(min) ? min : row?.[minKey],
        [maxKey]: Number.isFinite(max) ? max : row?.[maxKey],
        [stepKey]: this.resolveApaSliderStep(min, max),
      };
    });
  }

  private extractApaRanges(source: any, popupConfig: SmartTablePopupConfig): any[] {
    const direct = Array.isArray(source?.responseList?.[0]?.data)
      ? source.responseList[0].data
      : Array.isArray(source?.data)
        ? source.data
        : Array.isArray(source)
          ? source
          : [];

    if (!Array.isArray(direct)) {
      return [];
    }

    const valueKey = popupConfig.apaRangeValueKey || 'currentValue';
    const overrides = this.readApaOverridesMap();

    return direct.map((row) => {
      const label = row?.label;
      const override = this.resolveStoredApaOverride(overrides[this.normalizeApaLabel(label)]);
      const fallbackValue = row?.currentValue ?? row?.value;
      return {
        ...row,
        min: override?.['min'] ?? row?.min,
        max: override?.['max'] ?? row?.max,
        [valueKey]: override?.['value'] ?? fallbackValue,
      };
    });
  }

  private readApaOverridesMap(): Record<string, unknown> {
    try {
      const raw = sessionStorage.getItem(JsonDrivenSmartTableComponent.APA_OVERRIDES_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private resolveStoredApaOverride(override: unknown): Record<string, unknown> | undefined {
    if (override && typeof override === 'object' && !Array.isArray(override)) {
      return override as Record<string, unknown>;
    }

    if (Number.isFinite(Number(override))) {
      return { value: Number(override) };
    }

    return undefined;
  }

  private normalizeApaLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private clampPopupSliderValue(value: any, min: number, max: number): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return Number.isFinite(min) ? min : 0;
    }

    if (Number.isFinite(min) && numericValue < min) {
      return min;
    }

    if (Number.isFinite(max) && numericValue > max) {
      return max;
    }

    return numericValue;
  }

  private resolveApaSliderStep(min: number, max: number): number {
    const range = max - min;
    if (!Number.isFinite(range) || range <= 0) {
      return 0.01;
    }

    const suggested = range / 100;
    return suggested >= 0.01 ? Number(suggested.toFixed(2)) : 0.01;
  }

  private mergeWidgetConfigs(remoteConfig: any, inputConfig: SmartTableConfig): SmartTableConfig {
    const remote = remoteConfig && typeof remoteConfig === 'object' ? remoteConfig as SmartTableConfig : {};
    return {
      ...remote,
      ...inputConfig,
      columns: Array.isArray(inputConfig.columns) && inputConfig.columns.length > 0 ? inputConfig.columns : remote.columns,
      columnBindings: this.mergePlainObjects(remote.columnBindings, inputConfig.columnBindings),
      rows: Array.isArray(inputConfig.rows) && inputConfig.rows.length > 0 ? inputConfig.rows : remote.rows,
      headerValues: this.mergePlainObjects(remote.headerValues, inputConfig.headerValues),
      rowPopup: this.mergePlainObjects(remote.rowPopup, inputConfig.rowPopup),
      recalculation: this.mergePlainObjects(remote.recalculation, inputConfig.recalculation),
      submitAction: this.mergePlainObjects(remote.submitAction, inputConfig.submitAction),
      themeOverrides: this.mergePlainObjects(remote.themeOverrides, inputConfig.themeOverrides),
    };
  }

  private mergePlainObjects<T>(base: T | undefined, override: T | undefined): T | undefined {
    if (!base) {
      return override;
    }
    if (!override) {
      return base;
    }

    const merged = { ...(base as Record<string, any>) };
    Object.keys(override as Record<string, any>).forEach((key) => {
      const overrideValue = (override as Record<string, any>)[key];
      const baseValue = (base as Record<string, any>)[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        merged[key] = this.mergePlainObjects(baseValue, overrideValue);
      } else {
        merged[key] = overrideValue;
      }
    });
    return merged as T;
  }

  private applyResolvedTheme(): void {
    const overrides = this.widgetConfig?.themeOverrides || this.inputData?.themeOverrides;
    const resolvedTheme = this.themeService.resolve(overrides);
    const host = this.hostElement.nativeElement;
    this.themeService.applyThemeTokensTo(resolvedTheme, host);

    const configuredFontScale = Number(this.widgetConfig?.fontScale ?? this.inputData?.fontScale);
    if (Number.isFinite(configuredFontScale) && configuredFontScale > 0) {
      host.style.setProperty('--dvm-font-scale', String(configuredFontScale));
    } else {
      host.style.removeProperty('--dvm-font-scale');
    }
  }

  private resolveMethod(url: string, explicitMethod?: string): string {
    if (explicitMethod) {
      return explicitMethod;
    }
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  private resolveNumericSetting(row: any, key: string | undefined, fallback: number | undefined, defaultValue: number): number {
    const fromRow = key ? Number(this.getValueByPath(row, key)) : NaN;
    if (Number.isFinite(fromRow)) {
      return fromRow;
    }

    const fromConfig = Number(fallback);
    if (Number.isFinite(fromConfig)) {
      return fromConfig;
    }

    return defaultValue;
  }

  private getDefaultSliderStep(column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column);
    if (decimals <= 0) {
      return 1;
    }

    return 1 / (10 ** decimals);
  }

  private resolveProgressValue(row: any, column: SmartTableColumn): number {
    const key = column.progressValueKey || column.key;
    return this.toFiniteNumber(this.getValueByPath(row, key));
  }

  private resolveProgressMax(row: any, column: SmartTableColumn): number {
    if (column.progressMaxKey) {
      const maxFromRow = Number(this.getValueByPath(row, column.progressMaxKey));
      if (Number.isFinite(maxFromRow) && maxFromRow > 0) {
        return maxFromRow;
      }
    }

    const maxFromConfig = Number(column.progressMax);
    return Number.isFinite(maxFromConfig) && maxFromConfig > 0 ? maxFromConfig : 100;
  }

  isNumberLikeColumn(column: SmartTableColumn): boolean {
    return column.type === 'number' || column.type === 'currency' || column.type === 'slider' || column.type === 'progress';
  }

  private getRowId(row: any, rowIndex: number): string {
    const explicitId = row?.id ?? row?._id ?? row?.rowId ?? row?.uuid ?? row?.customer_name;
    return explicitId !== undefined && explicitId !== null && explicitId !== ''
      ? String(explicitId)
      : `row-${rowIndex}`;
  }

  // Re-applies the scrollTop captured just before a reload in loadRowsFromConfig(). The
  // `.table-scroll` element is destroyed and recreated by the `*ngIf="!loading && ..."` in
  // the template, so a fresh element (scrollTop 0) exists by the time this runs — deferring
  // to the next animation frame lets that new element finish painting first.
  private restoreScrollIfPending(): void {
    if (this.pendingScrollRestoreTop === null) {
      return;
    }

    const restoreTop = this.pendingScrollRestoreTop;
    this.pendingScrollRestoreTop = null;
    requestAnimationFrame(() => {
      const scrollEl = this.hostElement.nativeElement.querySelector('.table-scroll');
      if (scrollEl instanceof HTMLElement) {
        scrollEl.scrollTop = restoreTop;
      }
    });
  }

  private resolveColumnPixelWidth(width: string | undefined): number {
    const raw = String(width || '').trim().toLowerCase();
    if (!raw) {
      return 0;
    }

    if (/^\d+(\.\d+)?px$/.test(raw)) {
      return Number.parseFloat(raw);
    }

    if (/^\d+(\.\d+)?rem$/.test(raw)) {
      return Number.parseFloat(raw) * 16;
    }

    if (/^\d+(\.\d+)?$/.test(raw)) {
      return Number.parseFloat(raw);
    }

    return 0;
  }

  private formatCurrencyValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    const rawPrefix = String(column.prefix || '$');
    const prefix = rawPrefix ? (rawPrefix.endsWith(' ') ? rawPrefix : `${rawPrefix} `) : '';
    const suffix = String(column.suffix || '');
    if (compact) {
      return `${prefix}${compact}${suffix}`;
    }

    const decimals = this.getColumnDecimals(column, 2);
    const numericValue = this.toFiniteNumber(value);
    return `${prefix}${this.formatNumberWithCommas(numericValue, decimals)}${suffix}`;
  }

  private formatNumericValue(value: any, column: SmartTableColumn, row?: any): string {
    const compact = this.formatCompactNumber(value, column, row);
    const rawPrefix = String(column.prefix || '');
    const prefix = rawPrefix ? (rawPrefix.endsWith(' ') ? rawPrefix : `${rawPrefix} `) : '';
    const suffix = String(column.suffix || '');
    if (compact) {
      return `${prefix}${compact}${suffix}`;
    }

    const numericValue = this.toFiniteNumber(value);
    const decimals = this.getColumnDecimals(column, 0);
    return `${prefix}${this.formatNumberWithCommas(numericValue, decimals)}${suffix}`;
  }

  private formatNumberWithCommas(value: number, decimals: number): string {
    if (decimals <= 0) {
      return commaSeparatedValue(value);
    }

    if (decimals === 1) {
      return oneDecimalWithCommas(value);
    }

    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  private formatCompactNumber(value: any, column: SmartTableColumn, row?: any): string | null {
    const numericValue = this.toFiniteNumber(value);
    const unit = this.resolveCompactUnit(column, numericValue, row);
    if (!unit) {
      return null;
    }

    const divisor = unit === 'm' ? 1_000_000 : 1_000;
    const scaled = numericValue / divisor;
    const configuredDecimals = Number(column.compactDecimals);
    const decimals = Number.isFinite(configuredDecimals) && configuredDecimals >= 0
      ? Math.floor(configuredDecimals)
      : 1;

    return `${this.trimTrailingZeros(scaled.toFixed(decimals))}${unit.toUpperCase()}`;
  }

  private resolveCompactUnit(column: SmartTableColumn, numericValue: number, row?: any): 'k' | 'm' | null {
    if (!this.shouldUseCompactFormat(column, row)) {
      return null;
    }

    const fromRow = column.compactUnitKey
      ? this.normalizeCompactUnit(this.getValueByPath(row, column.compactUnitKey))
      : null;
    const configuredUnit = this.normalizeCompactUnit(column.compactUnit);
    const unit = fromRow || configuredUnit || 'auto';

    if (unit === 'k' || unit === 'm') {
      return unit;
    }

    const absolute = Math.abs(numericValue);
    if (absolute >= 1_000_000) {
      return 'm';
    }

    if (absolute >= 1_000) {
      return 'k';
    }

    return null;
  }

  private shouldUseCompactFormat(column: SmartTableColumn, row?: any): boolean {
    const fromRow = column.compactFormatKey
      ? this.toBooleanFlag(this.getValueByPath(row, column.compactFormatKey))
      : null;
    if (fromRow !== null) {
      return fromRow;
    }

    if (typeof column.compactFormat === 'boolean') {
      return column.compactFormat;
    }

    return !!column.compactUnit || !!column.compactUnitKey;
  }

  private normalizeCompactUnit(value: unknown): 'auto' | 'k' | 'm' | null {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'k' || normalized === 'm' || normalized === 'auto') {
      return normalized;
    }

    return null;
  }

  private toBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private trimTrailingZeros(value: string): string {
    return value
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '');
  }

  private normalizeNumericColumnValue(value: number, column: SmartTableColumn): number {
    const decimals = this.getColumnDecimals(column, 0);
    if (!Number.isFinite(value)) {
      return 0;
    }

    if (decimals <= 0) {
      return Math.round(value);
    }

    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  private parseNumber(value: string): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toFiniteNumber(value: any): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    // If value is a string, try to extract numeric part (handles "21%", "$123.45", etc.)
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d.-]/g, '');
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private roundValue(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  private getColumnDecimals(column: SmartTableColumn, fallback = 0): number {
    const raw = Number(column.decimals);
    if (!Number.isFinite(raw) || raw < 0) {
      return fallback;
    }

    return Math.floor(raw);
  }

  private normalizeCssSize(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : String(value);
  }

  private resolveConfiguredTableHeight(
    config: Pick<SmartTableConfig | SmartTablePopupConfig, 'tableHeight' | 'height'>,
    fallback: string,
  ): string {
    const configured = config.tableHeight ?? config.height ?? fallback;
    return this.normalizeCssSize(configured);
  }

  private getValueByPath(source: any, path: string): any {
    if (!path) {
      return source;
    }

    return String(path)
      .split('.')
      .reduce((value: any, part: string) => (value == null ? undefined : value[part]), source);
  }

  private humanize(value: string): string {
    return String(value)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (segment) => segment.toUpperCase());
  }

  private cloneObject<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneObject(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }

    return value;
  }

  private ensureDropdownOptions(rowIndex: number, row: any, column: SmartTableColumn): void {
    const staticOptions = this.extractStaticOptions(column);
    if (staticOptions.length > 0 || !column.dropdownApiUrl) {
      return;
    }

    const cacheKey = this.buildDropdownCacheKey(rowIndex, row, column);
    if (this.dropdownOptionsCache.has(cacheKey) || this.dropdownLoadingKeys.has(cacheKey)) {
      return;
    }

    const apiUrl = String(this.resolveDynamicValue(column.dropdownApiUrl, { row, column }) || '');
    if (!apiUrl) {
      return;
    }

    const method = this.resolveMethod(apiUrl, column.dropdownMethod);
    const requestPayload = this.resolveDynamicValue(column.dropdownRequestPayload, { row, column });
    const queryParams = this.resolveDynamicValue(column.dropdownQueryParams, { row, column });
    const filters = this.buildRequestFilters(requestPayload, queryParams);
    const responsePath = String(this.resolveDynamicValue(column.dropdownResponsePath, { row, column }) || '');

    this.dropdownLoadingKeys.add(cacheKey);

    this.configService
      .getConfig(apiUrl, column, filters, method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const source = responsePath ? this.getValueByPath(response, responsePath) : response;
          this.dropdownOptionsCache.set(cacheKey, this.normalizeDropdownOptions(source, column));
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
        error: () => {
          this.dropdownLoadingKeys.delete(cacheKey);
          this.cdr.markForCheck();
        },
      });
  }

  private extractStaticOptions(column: SmartTableColumn): SmartTableOption[] {
    return this.normalizeDropdownOptions(column.options || [], column);
  }

  private prioritizeCurrentDropdownOption(
    options: SmartTableOption[],
    row: any,
    column: SmartTableColumn,
  ): SmartTableOption[] {
    if (!column.dropdownCurrentValueFirst || !Array.isArray(options) || options.length < 2) {
      return options;
    }

    const currentValue = this.normalizeDropdownComparable(this.getDropdownValue(row, column));
    if (!currentValue) {
      return options;
    }

    const matchedOption = options.find((option) =>
      this.normalizeDropdownComparable(option.value) === currentValue ||
      this.normalizeDropdownComparable(option.label) === currentValue,
    );

    if (!matchedOption) {
      const displayValue = this.getDropdownValue(row, column);
      return [
        { label: displayValue, value: displayValue },
        ...options,
      ];
    }

    return [
      matchedOption,
      ...options.filter((option) => option !== matchedOption),
    ];
  }

  private normalizeDropdownComparable(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private normalizeDropdownOptions(source: any, column: SmartTableColumn): SmartTableOption[] {
    const candidate = Array.isArray(source)
      ? source
      : source?.options || source?.dropdownOptions || source?.items || source?.data || [];

    if (!Array.isArray(candidate)) {
      return [];
    }

    const labelKey = column.dropdownLabelKey || 'label';
    const valueKey = column.dropdownValueKey || 'value';
    const disabledKey = column.dropdownDisabledKey || 'disabled';

    return candidate.map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const label = item[labelKey] ?? item[valueKey] ?? item['name'] ?? item['title'] ?? '';
        const value = item[valueKey] ?? item[labelKey] ?? label;
        return {
          label: String(label),
          value,
          disabled: Boolean(item[disabledKey]),
        };
      }

      return {
        label: String(item),
        value: item,
        disabled: false,
      };
    });
  }

  private buildDropdownCacheKey(rowIndex: number, row: any, column: SmartTableColumn): string {
    const rowKey = column.dropdownRequestPayload || column.dropdownQueryParams ? this.getRowId(row, rowIndex) : 'shared';
    return `${column.bindingKey || column.key}::${rowKey}`;
  }

  private resolveDropdownValue(rawValue: string, rowIndex: number, row: any, column: SmartTableColumn): any {
    const options = this.getDropdownOptions(rowIndex, row, column);
    const normalizedRawValue = this.normalizeDropdownComparable(rawValue);
    const matched = options.find((option) =>
      this.normalizeDropdownComparable(option.value) === normalizedRawValue ||
      this.normalizeDropdownComparable(option.label) === normalizedRawValue,
    );
    return matched ? matched.value : rawValue;
  }

  private buildRequestFilters(requestPayload: any, queryParams: any): any {
    const filters: any = {};

    if (requestPayload !== undefined && requestPayload !== null && requestPayload !== '') {
      filters.requestParams = requestPayload;
    }

    if (queryParams && typeof queryParams === 'object' && !Array.isArray(queryParams)) {
      filters.queryParams = queryParams;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private resolvePopupRequestPayload(
    apiUrl: string,
    popupConfig: SmartTablePopupConfig,
    row: any,
    column?: SmartTableColumn,
  ): any {
    const configuredPayload = this.resolveDynamicValue(popupConfig.requestPayload, { row, column });
    const defaultPayload = this.getDefaultPopupRequestPayload(apiUrl, row, column);
    const mode = String(popupConfig.requestPayloadMode || '').trim().toLowerCase();

    if (mode === 'none') {
      return undefined;
    }

    if (mode === 'custom') {
      return configuredPayload;
    }

    if (mode === 'merge') {
      return this.mergePayloadValues(defaultPayload, configuredPayload);
    }

    if (mode === 'auto') {
      return defaultPayload;
    }

    return configuredPayload ?? defaultPayload;
  }

  private getDefaultPopupRequestPayload(apiUrl: string, row: any, column?: SmartTableColumn): any {
    if (!apiUrl || this.isLocalAssetUrl(apiUrl)) {
      return undefined;
    }

    const clickedRow = this.cloneObject(row);
    const payload: Record<string, any> = {
      ...clickedRow,
      clickedRow,
      clickedRowId: this.getRowId(row, 0),
    };

    if (column?.key) {
      payload['clickedColumnKey'] = column.key;
      payload['clickedColumnValue'] = this.getValueByPath(row, column.key);
      payload[column.key] = this.getValueByPath(row, column.key);
    }

    return payload;
  }

  private mergePayloadValues(base: any, override: any): any {
    if (base == null) {
      return override;
    }
    if (override == null) {
      return base;
    }

    if (
      base &&
      typeof base === 'object' &&
      !Array.isArray(base) &&
      override &&
      typeof override === 'object' &&
      !Array.isArray(override)
    ) {
      return {
        ...base,
        ...override,
      };
    }

    return override;
  }

  private resolvePopupHeaderActions(
    popupConfig: SmartTablePopupConfig,
    row: any,
    column: SmartTableColumn | undefined,
  ): SmartTablePopupAction[] {
    const actions = Array.isArray(popupConfig.headerActions) ? popupConfig.headerActions : [];
    return actions
      .map((action) => ({
        ...action,
        label: String(this.resolveDynamicValue(action.label || '', { row, column }) || ''),
        actionType: String(this.resolveDynamicValue(action.actionType || '', { row, column }) || '').toLowerCase() === 'export'
          ? 'export' as const
          : 'navigate' as const,
        navigateAction: this.resolveDynamicValue(action.navigateAction, { row, column }),
        fileName: this.resolveExportFileName(action.fileName, row?.customer_name || popupConfig.title || 'table-export', { row, column }),
      }))
      .filter((action) => !!action.label);
  }

  private exportTable(target: 'main' | 'popup' | 'inline', action: SmartTablePopupAction): void {
    const exportState =
      target === 'popup'
        ? this.popupTable
        : target === 'inline'
          ? this.inlineDetailTable
          : {
              title: this.widgetConfig?.title || 'Table Export',
              columns: this.columns,
              rows: this.rows,
            };

    const exportColumns = Array.isArray(exportState.columns) ? exportState.columns : [];
    const exportRows = Array.isArray(exportState.rows) ? exportState.rows : [];
    if (exportColumns.length === 0 || exportRows.length === 0) {
      return;
    }

    const headerCells = exportColumns.map((column) => this.escapeHtml(this.getHeaderLabel(column, target === 'main' ? 'main' : target)));
    const bodyRows = exportRows.map((row) =>
      exportColumns.map((column) => this.escapeHtml(this.getPlainExportValue(row, column))),
    );

    const html = [
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">',
      '<head>',
      '<meta charset="utf-8" />',
      '<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->',
      '</head>',
      '<body>',
      '<table border="1">',
      `<thead><tr>${headerCells.map((value) => `<th>${value}</th>`).join('')}</tr></thead>`,
      `<tbody>${bodyRows.map((cells) => `<tr>${cells.map((value) => `<td>${value}</td>`).join('')}</tr>`).join('')}</tbody>`,
      '</table>',
      '</body>',
      '</html>',
    ].join('');

    const blob = new Blob(['\ufeff', html], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });

    saveAs(blob, this.ensureExcelFileName(action.fileName || exportState.title || 'table-export'));
  }

  private getPlainExportValue(row: any, column: SmartTableColumn): string {
    const rawValue = this.getValueByPath(row, column.key);
    if (rawValue === null || rawValue === undefined) {
      return '';
    }

    if (typeof rawValue === 'object') {
      return JSON.stringify(rawValue);
    }

    return String(rawValue);
  }

  private resolveExportFileName(
    configuredFileName: any,
    fallback: string,
    context: SmartTableTemplateContext = {},
  ): string {
    const resolved = this.resolveDynamicValue(configuredFileName, context);
    return this.ensureExcelFileName(String(resolved || fallback || 'table-export'));
  }

  private ensureExcelFileName(fileName: string): string {
    const trimmed = String(fileName || 'table-export').trim() || 'table-export';
    const sanitized = trimmed.replace(/[\\/:*?"<>|]+/g, '_');
    return /\.xlsx?$/i.test(sanitized) ? sanitized : `${sanitized}.xls`;
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private withResolvedProductPriceType(navigateAction: any, row: any, filters: any): any {
    if (!navigateAction || typeof navigateAction !== 'object') {
      return navigateAction;
    }

    const resolved = this.cloneObject(navigateAction);
    const rowProductPriceType =
      row?.product_price_type ??
      row?.productPriceType ??
      row?.label;
    const filterProductPriceType =
      filters?.requestParams?.product_price_type ??
      filters?.queryParams?.product_price_type;
    const productPriceType = rowProductPriceType ?? filterProductPriceType;

    if (productPriceType === undefined || productPriceType === null || productPriceType === '') {
      return resolved;
    }

    if (resolved?.payload?.requestData && typeof resolved.payload.requestData === 'object') {
      resolved.payload.requestData = {
        ...resolved.payload.requestData,
        product_price_type: productPriceType,
      };
    }

    if (resolved?.payload?.requestParams && typeof resolved.payload.requestParams === 'object') {
      resolved.payload.requestParams = {
        ...resolved.payload.requestParams,
        product_price_type: productPriceType,
      };
    }

    return resolved;
  }

  private resolveFormulaKeys(recalculation?: SmartTableRecalculationConfig): SmartTableFormulaConfig {
    const formula = recalculation?.formula || {};
    const inputKeys = recalculation?.inputKeys || {};

    return {
      confidenceKey: formula.confidenceKey || inputKeys.confidence,
      priceLiftKey: formula.priceLiftKey || inputKeys.priceLift,
      riskKey: formula.riskKey || inputKeys.risk,
      resourceLoadKey: formula.resourceLoadKey || inputKeys.resourceLoad,
      baseValueKey: formula.baseValueKey || inputKeys.baseRevenue,
    };
  }

  private resolveOutputKeys(recalculation?: SmartTableRecalculationConfig): {
    score?: string;
    scoreColor?: string;
    projection?: string;
    status?: string;
    statusColor?: string;
    syncState?: string;
    syncColor?: string;
  } {
    const outputKeys = recalculation?.outputKeys || {};
    return {
      score: outputKeys.score || recalculation?.scoreKey,
      scoreColor: outputKeys.scoreColor || recalculation?.scoreColorKey,
      projection: outputKeys.projection || recalculation?.projectionKey,
      status: outputKeys.status || recalculation?.statusKey,
      statusColor: outputKeys.statusColor || recalculation?.statusColorKey,
      syncState: outputKeys.syncState || recalculation?.syncKey,
      syncColor: outputKeys.syncColor || recalculation?.syncColorKey,
    };
  }

  private createEmptyTableState(scope: SmartTableScope): SmartTableViewState {
    return {
      scope,
      title: '',
      columns: [],
      rows: [],
      loading: false,
      errorMessage: '',
      tableHeight: scope === 'main' ? this.tableHeight : '22rem',
      emptyStateMessage: scope === 'main' ? this.emptyStateMessage : 'No detail rows available.',
      headerValues: undefined,
      headerActions: [],
      showColumnDividers: false,
      dividerColumns: [],
    };
  }

  private getShowColumnDividers(scope: SmartTableScope): boolean {
    if (scope === 'popup') {
      return this.popupTable.showColumnDividers === true;
    }

    if (scope === 'inline') {
      return this.inlineDetailTable.showColumnDividers === true;
    }

    return this.widgetConfig?.showColumnDividers === true;
  }

  private getDividerColumns(scope: SmartTableScope): string[] {
    const configuredColumns = scope === 'popup'
      ? this.popupTable.dividerColumns
      : scope === 'inline'
        ? this.inlineDetailTable.dividerColumns
        : this.widgetConfig?.dividerColumns;

    return Array.isArray(configuredColumns)
      ? configuredColumns.map((value) => String(value || '').trim()).filter(Boolean)
      : [];
  }

  private loadRowsFromConfig(config: SmartTableConfig, filters?: any): void {
    const scrollEl = this.hostElement.nativeElement.querySelector('.table-scroll');
    this.pendingScrollRestoreTop = scrollEl instanceof HTMLElement ? scrollEl.scrollTop : null;

    const inlineRows = this.extractRowsFromSource(config.rows, config);
    if (inlineRows.length > 0) {
      this.debug('loadRowsFromConfig inline rows', {
        widgetId: config.widgetId,
        rowCount: inlineRows.length,
      });
      this.applyRowsForDisplay(inlineRows, config);
      return;
    }

    const rowsUrl = config.apiUrl || config.mockDataUrl;
    if (!rowsUrl) {
      this.debug('loadRowsFromConfig no url', {
        widgetId: config.widgetId,
      });
      this.rows = [];
      this.loading = false;
      this.errorMessage = '';
      this.syncMessage = this.getPendingSyncMessage();
      this.cdr.markForCheck();
      this.restoreScrollIfPending();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.debug('loadRowsFromConfig request', {
      widgetId: config.widgetId,
      rowsUrl,
      method: this.resolveMethod(rowsUrl, config.method),
      filters: this.summarizeFilters(filters),
    });
    this.cdr.markForCheck();

    this.configService
      .getConfig(rowsUrl, config, filters, this.resolveMethod(rowsUrl, config.method))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.debug('loadRowsFromConfig success', {
            widgetId: config.widgetId,
            responseKeys: Object.keys(response || {}),
            extractedRowCount: this.extractRowsFromSource(response, config).length,
          });
          const extractedRows = this.extractRowsFromSource(response, config);
          this.applyRowsForDisplay(extractedRows, config);
        },
        error: (error) => {
          this.debug('loadRowsFromConfig error', {
            widgetId: config.widgetId,
            error,
          });
          this.loading = false;
          this.errorMessage = 'Unable to load table rows.';
          this.cdr.markForCheck();
        },
      });
  }

  private applyRowsForDisplay(rows: any[], config: SmartTableConfig): void {
    const loadVersion = ++this.rowLoadVersion;
    const sortedRows = this.sortRowsForDisplay(this.normalizePlanningStatusRows(rows), config);
    this.rows = sortedRows;
    this.loading = false;
    this.errorMessage = '';
    this.syncMessage = this.getPendingSyncMessage();
    this.cdr.markForCheck();
    this.restoreScrollIfPending();

    if (this.pendingInlineDetailScrollRestore && this.inlineDetailRowId) {
      this.pendingInlineDetailScrollRestore = false;
      this.scheduleInlineDetailScroll();
    }

    const sourceRows = sortedRows;

    if (config?.inheritApaPricingLever && Array.isArray(sortedRows) && sortedRows.length > 0) {
      this.resolveInitialRowsForDisplay(sourceRows, config)
        .pipe(takeUntil(this.destroy$))
        .subscribe((resolvedRows) => {
          if (loadVersion !== this.rowLoadVersion) {
            return;
          }

          this.rows = this.sortRowsForDisplay(resolvedRows, config);
          this.cdr.markForCheck();
        });
    }

    this.applyColumnValueApis(sourceRows, config, loadVersion);
  }

  private normalizePlanningStatusRows(rows: any[]): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return row;
      }

      const planningStatus = String(row?.planning_status ?? '').trim();
      if (!planningStatus) {
        return row;
      }

      return {
        ...row,
        status: planningStatus,
      };
    });
  }

  private applyColumnValueApis(sourceRows: any[], config: SmartTableConfig, loadVersion: number): void {
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
      return;
    }

    const lookupColumns = (this.columns || []).filter((column) => {
      const key = String(column?.key || '').trim();
      return !!key && !!String(column?.valueApiUrl || '').trim();
    });

    if (lookupColumns.length === 0) {
      return;
    }

    lookupColumns.forEach((column) => {
      const apiUrl = String(column.valueApiUrl || '').trim();
      this.loadColumnValueApiSource(apiUrl, column)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          if (loadVersion !== this.rowLoadVersion) {
            return;
          }

          const rowMatchKey = column.valueApiRowMatchKey || config.apaRangeProductTypeKey || 'product_price_type';
          const responseMatchKey = column.valueApiResponseMatchKey || 'label';
          const responseValueKey = column.valueApiResponseValueKey || 'value';
          const valueByMatchKey = this.buildColumnValueApiLookup(response, column, responseMatchKey, responseValueKey);

          if (valueByMatchKey.size === 0) {
            return;
          }

          let changed = false;
          const nextRows = this.rows.map((row) => {
            const matchKey = this.normalizeColumnValueApiKey(this.getValueByPath(row, rowMatchKey));
            if (!matchKey || !valueByMatchKey.has(matchKey)) {
              return row;
            }

            const nextValue = valueByMatchKey.get(matchKey);
            if (row?.[column.key] === nextValue) {
              return row;
            }

            changed = true;
            return {
              ...row,
              [column.key]: nextValue,
            };
          });

          if (changed) {
            this.rows = this.sortRowsForDisplay(nextRows, config);
            this.cdr.markForCheck();
          }
        });
    });
  }

  private sortRowsForDisplay(rows: any[], config: SmartTableConfig | SmartTablePopupConfig): any[] {
    if (!Array.isArray(rows) || rows.length < 2) {
      return rows;
    }

    const sortKey = this.resolveRowSortKey(config);
    if (!sortKey) {
      return rows;
    }

    const direction = this.resolveRowSortDirection(config);
    return [...rows].sort((left, right) => {
      const comparison = this.compareRowSortValues(
        this.getValueByPath(left, sortKey),
        this.getValueByPath(right, sortKey),
      );
      return direction === 'desc' ? -comparison : comparison;
    });
  }

  private resolveRowSortKey(config: SmartTableConfig | SmartTablePopupConfig): string {
    return String(
      config?.rowSortKey ??
      config?.sortKey ??
      config?.orderKey ??
      '',
    ).trim();
  }

  private resolveRowSortDirection(config: SmartTableConfig | SmartTablePopupConfig): 'asc' | 'desc' {
    const direction = String(
      config?.rowSortDirection ??
      config?.sortDirection ??
      config?.orderDirection ??
      'asc',
    ).trim().toLowerCase();

    return direction === 'desc' || direction === 'descending' ? 'desc' : 'asc';
  }

  private compareRowSortValues(left: any, right: any): number {
    const leftNumber = this.parseRowSortNumber(left);
    const rightNumber = this.parseRowSortNumber(right);

    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
      return leftNumber - rightNumber;
    }

    if (Number.isFinite(leftNumber)) {
      return 1;
    }

    if (Number.isFinite(rightNumber)) {
      return -1;
    }

    return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private parseRowSortNumber(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    const text = String(value ?? '').trim();
    if (!text) {
      return Number.NaN;
    }

    const normalized = text.replace(/[$,%\s,]/g, '').toLowerCase();
    const match = normalized.match(/^(-?\d+(?:\.\d+)?)([kmb])?$/);
    if (!match) {
      return Number.NaN;
    }

    const numericValue = Number(match[1]);
    if (!Number.isFinite(numericValue)) {
      return Number.NaN;
    }

    const suffix = match[2];
    if (suffix === 'k') return numericValue * 1_000;
    if (suffix === 'm') return numericValue * 1_000_000;
    if (suffix === 'b') return numericValue * 1_000_000_000;
    return numericValue;
  }

  private loadColumnValueApiSource(apiUrl: string, column: SmartTableColumn): Observable<any> {
    const requestConfig = column.valueApiRequestConfig;
    const method = this.resolveMethod(
      apiUrl,
      column.valueApiMethod || requestConfig?.method || (requestConfig ? 'post' : undefined),
    );
    const cacheKey = `${method.toUpperCase()}|${apiUrl}|${JSON.stringify(requestConfig || {})}`;
    const cached = this.columnValueApiCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.configService.getConfig(apiUrl, requestConfig || {}, undefined, method).pipe(
      catchError(() => {
        this.columnValueApiCache.delete(cacheKey);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.columnValueApiCache.set(cacheKey, request$);
    return request$;
  }

  private buildColumnValueApiLookup(
    source: any,
    column: SmartTableColumn,
    responseMatchKey: string,
    responseValueKey: string,
  ): Map<string, any> {
    const valueByMatchKey = new Map<string, any>();
    const useFirstRecord = column.valueApiUseFirstRecord === true || !!column.valueApiResponseValueMap;
    const lookupRows = useFirstRecord ? [] : this.extractColumnValueApiRows(source, column);

    lookupRows.forEach((lookupRow) => {
      const matchKey = this.normalizeColumnValueApiKey(
        this.getValueByPath(lookupRow, responseMatchKey) ??
          lookupRow?.label ??
          lookupRow?.product_price_type ??
          lookupRow?.productPriceType ??
          lookupRow?.name ??
          lookupRow?.type,
      );
      if (matchKey) {
        valueByMatchKey.set(
          matchKey,
          this.getValueByPath(lookupRow, responseValueKey) ??
            lookupRow?.currentValue ??
            lookupRow?.value ??
            lookupRow?.max ??
            lookupRow?.min,
        );
      }
    });

    const valueObject = this.extractColumnValueApiObject(source, column);
    const configuredMap = column.valueApiResponseValueMap || {};
    Object.entries(configuredMap).forEach(([productType, responseKey]) => {
      const matchKey = this.normalizeColumnValueApiKey(productType);
      const value = this.getValueByPath(valueObject, responseKey) ?? this.findDeepValueByKey(valueObject, responseKey);
      if (matchKey && value !== undefined && value !== null && value !== '') {
        valueByMatchKey.set(matchKey, value);
      }
    });

    this.collectPrimitiveEntries(valueObject).forEach(([responseKey, value]) => {
      if (value === undefined || value === null || value === '' || typeof value === 'object') {
        return;
      }

      const matchKey = this.normalizeColumnValueApiKey(responseKey);
      if (matchKey && !valueByMatchKey.has(matchKey)) {
        valueByMatchKey.set(matchKey, value);
      }
    });

    return valueByMatchKey;
  }

  private findDeepValueByKey(source: any, targetKey: string): any {
    const normalizedTarget = this.normalizeColumnValueApiKey(targetKey);
    if (!normalizedTarget || source === null || source === undefined) {
      return undefined;
    }

    const visited = new WeakSet<object>();
    const search = (value: any): any => {
      if (!value || typeof value !== 'object') {
        return undefined;
      }

      if (visited.has(value)) {
        return undefined;
      }
      visited.add(value);

      if (Array.isArray(value)) {
        for (const item of value) {
          const found = search(item);
          if (found !== undefined) {
            return found;
          }
        }
        return undefined;
      }

      for (const [key, child] of Object.entries(value)) {
        if (this.normalizeColumnValueApiKey(key) === normalizedTarget && child !== null && child !== '') {
          return child;
        }
      }

      for (const child of Object.values(value)) {
        const found = search(child);
        if (found !== undefined) {
          return found;
        }
      }

      return undefined;
    };

    return search(source);
  }

  private collectPrimitiveEntries(source: any): Array<[string, any]> {
    const entries: Array<[string, any]> = [];
    const visited = new WeakSet<object>();
    const walk = (value: any): void => {
      if (!value || typeof value !== 'object') {
        return;
      }

      if (visited.has(value)) {
        return;
      }
      visited.add(value);

      if (Array.isArray(value)) {
        value.forEach((item) => walk(item));
        return;
      }

      Object.entries(value).forEach(([key, child]) => {
        if (child && typeof child === 'object') {
          walk(child);
          return;
        }

        entries.push([key, child]);
      });
    };

    walk(source);
    return entries;
  }

  private extractColumnValueApiRows(source: any, column: SmartTableColumn): any[] {
    const configuredPath = String(column.valueApiResponsePath || '').trim();
    if (configuredPath) {
      const resolved = this.getValueByPath(source, configuredPath);
      return Array.isArray(resolved) ? resolved : [];
    }

    if (Array.isArray(source?.responseList?.[0]?.data)) {
      return source.responseList[0].data;
    }

    if (Array.isArray(source?.data)) {
      return source.data;
    }

    if (Array.isArray(source)) {
      return source;
    }

    return [];
  }

  private extractColumnValueApiObject(source: any, column: SmartTableColumn): Record<string, any> {
    const configuredPath = String(column.valueApiResponsePath || '').trim();
    if (configuredPath) {
      const resolved = this.getValueByPath(source, configuredPath);
      if (Array.isArray(resolved) && resolved[0] && typeof resolved[0] === 'object') {
        return resolved[0];
      }
      if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
        return resolved;
      }
    }

    const firstResponseData = source?.responseList?.[0]?.data;
    if (Array.isArray(firstResponseData) && firstResponseData[0] && typeof firstResponseData[0] === 'object') {
      return firstResponseData[0];
    }

    if (firstResponseData && typeof firstResponseData === 'object' && !Array.isArray(firstResponseData)) {
      return firstResponseData;
    }

    if (Array.isArray(source?.data) && source.data[0] && typeof source.data[0] === 'object') {
      return source.data[0];
    }

    if (source?.data && typeof source.data === 'object' && !Array.isArray(source.data)) {
      return source.data;
    }

    if (Array.isArray(source) && source[0] && typeof source[0] === 'object') {
      return source[0];
    }

    if (source && typeof source === 'object' && !Array.isArray(source)) {
      return source;
    }

    return {};
  }

  private normalizeColumnValueApiKey(value: any): string {
    const normalized = this.normalizeApaLabel(value)
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (['mfn star', 'mfn'].includes(normalized)) {
      return 'mfn';
    }

    if (['key account', 'key accounts', 'key'].includes(normalized)) {
      return 'key accounts';
    }

    if (['strategic target', 'strategic'].includes(normalized)) {
      return 'strategic target';
    }

    if (['review others', 'review other', 'review'].includes(normalized)) {
      return 'review others';
    }

    return normalized;
  }

  private getInitialFiltersForWidget(config: SmartTableConfig): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(config?.widgetId);
    if (navigationFilters) {
      this.debug('getInitialFiltersForWidget navigation', {
        widgetId: config?.widgetId,
        filters: this.summarizeFilters(navigationFilters),
      });
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters || !config?.widgetId) {
      this.debug('getInitialFiltersForWidget empty', {
        widgetId: config?.widgetId,
        hasFilters,
      });
      return undefined;
    }

    const shouldReload = this.chartFilterHandler.shouldReload(filters, config.widgetId);
    this.debug('getInitialFiltersForWidget current', {
      widgetId: config.widgetId,
      shouldReload,
      filters: this.summarizeFilters(filters),
    });
    return shouldReload ? filters : undefined;
  }

  private debug(message: string, payload?: any): void {
    console.log(`${JsonDrivenSmartTableComponent.DEBUG_KEY} [SmartTable] ${message}`, payload);
  }

  private summarizeFilters(filters: any): any {
    if (!filters) {
      return filters;
    }

    return {
      widgetId: filters?.widgetId,
      filterListener: filters?.filterListener ?? filters?.filterListner,
      requestParams: filters?.requestParams,
      queryParams: filters?.queryParams,
      skipReload: filters?.skipReload,
      useQueryParams: filters?.useQueryParams,
    };
  }

  private resolveRowPopupConfig(scope: SmartTableScope, row: any, rowIndex: number): SmartTableRowPopupConfig | null {
    if (scope !== 'main') {
      return null;
    }

    const config = this.widgetConfig?.rowPopup;
    if (!config || config.enabled === false) {
      return null;
    }

    const rowIndexes = Array.isArray(config.rowIndexes) ? config.rowIndexes : [];
    if (this.matchesRowIndexSelector(rowIndexes, rowIndex)) {
      return config;
    }

    const rowIds = Array.isArray(config.rowIds) ? config.rowIds.map((value) => String(value)) : [];
    if (rowIds.length > 0) {
      const rowIdKey = config.rowIdKey || 'id';
      const candidateId = this.getValueByPath(row, rowIdKey);
      if (candidateId !== undefined && candidateId !== null && rowIds.includes(String(candidateId))) {
        return config;
      }
    }

    if (config.matchKey) {
      const rowValue = this.getValueByPath(row, config.matchKey);
      if (Array.isArray(config.matchValues) && config.matchValues.some((value) => String(value) === String(rowValue))) {
        return config;
      }
      if (config.matchValue !== undefined && String(config.matchValue) === String(rowValue)) {
        return config;
      }
    }

    return rowIndexes.length === 0 && rowIds.length === 0 && !config.matchKey ? config : null;
  }

  private matchesRowIndexSelector(indexes: Array<number | 'n' | 'N'>, rowIndex: number): boolean {
    if (indexes.length === 0) {
      return false;
    }

    const normalized = indexes
      .map((value) => this.normalizeRowIndexValue(value))
      .filter((value): value is number => Number.isFinite(value));

    if (normalized.includes(rowIndex)) {
      return true;
    }

    if (normalized.length === 2) {
      const [start, end] = normalized;
      const rangeStart = Math.min(start, end);
      const rangeEnd = Math.max(start, end);
      return rowIndex >= rangeStart && rowIndex <= rangeEnd;
    }

    return false;
  }

  private normalizeRowIndexValue(value: number | 'n' | 'N'): number {
    if (value === 'n' || value === 'N') {
      return Math.max(0, this.rows.length - 1);
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : NaN;
  }

  private isLocalAssetUrl(url: string): boolean {
    return url.startsWith('assets') || url.startsWith('/assets');
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, input, select, textarea, a, label');
  }

  private resolveDynamicValue(value: any, context: SmartTableTemplateContext): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item, context));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.resolveDynamicValue(value[key], context);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    if (value.startsWith('$row.')) {
      return this.getValueByPath(context.row, value.slice(5));
    }
    if (value === '$row') {
      return context.row;
    }
    if (value.startsWith('$column.')) {
      return this.getValueByPath(context.column, value.slice(8));
    }
    if (value === '$column') {
      return context.column;
    }
    if (value.startsWith('$event.')) {
      return this.getValueByPath(context.event, value.slice(7));
    }
    if (value === '$event') {
      return context.event;
    }
    if (value === '$rows') {
      return this.cloneObject(this.rows);
    }
    if (value === '$editedRow') {
      return this.cloneObject(context.editedRow ?? this.lastEditedRow);
    }
    if (value === '$editedRows') {
      return this.cloneObject(context.editedRows ?? []);
    }
    if (value === '$editedCellCount') {
      return this.editedCellCount;
    }
    if (value === '$editedValue') {
      return context.editedValue;
    }

    const exactMatch = value.match(/^\{\{\s*([^}]+)\s*\}\}$/);
    if (exactMatch) {
      return this.resolveDynamicTokenExpression(exactMatch[1], context);
    }

    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const resolved = this.resolveDynamicTokenExpression(token, context);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  private resolveDynamicTokenExpression(expression: string, context: SmartTableTemplateContext): any {
    const [token, ...transforms] = expression
      .split('|')
      .map((part) => part.trim())
      .filter((part) => !!part);
    const resolved = this.resolveDynamicToken(token || '', context);
    return transforms.reduce((current, transform) => this.applyDynamicTokenTransform(current, transform), resolved);
  }

  private applyDynamicTokenTransform(value: any, transform: string): any {
    const normalized = transform.trim().toLowerCase();
    if (normalized === 'lowercase' || normalized === 'lower') {
      return String(value ?? '').toLowerCase();
    }
    if (normalized === 'uppercase' || normalized === 'upper') {
      return String(value ?? '').toUpperCase();
    }
    if (normalized === 'trim') {
      return String(value ?? '').trim();
    }
    return value;
  }

  private resolveDynamicToken(token: string, context: SmartTableTemplateContext): any {
    const normalized = token.trim();

    if (normalized === 'row') {
      return context.row;
    }
    if (normalized.startsWith('row.')) {
      return this.getValueByPath(context.row, normalized.slice(4));
    }
    if (normalized === 'column') {
      return context.column;
    }
    if (normalized.startsWith('column.')) {
      return this.getValueByPath(context.column, normalized.slice(7));
    }
    if (normalized === 'rows') {
      return this.cloneObject(this.rows);
    }
    if (normalized === 'editedRow') {
      return this.cloneObject(context.editedRow ?? this.lastEditedRow);
    }
    if (normalized.startsWith('editedRow.')) {
      return this.getValueByPath(context.editedRow ?? this.lastEditedRow, normalized.slice(10));
    }
    if (normalized === 'editedRows') {
      return this.cloneObject(context.editedRows ?? []);
    }
    if (normalized === 'editedCellCount') {
      return this.editedCellCount;
    }
    if (normalized === 'editedValue') {
      return context.editedValue;
    }
    if (normalized === 'widget') {
      return this.widgetConfig;
    }
    if (normalized.startsWith('widget.')) {
      return this.getValueByPath(this.widgetConfig, normalized.slice(7));
    }
    if (normalized === 'event') {
      return context.event;
    }
    if (normalized.startsWith('event.')) {
      return this.getValueByPath(context.event, normalized.slice(6));
    }
    if (normalized === 'currentUser') {
      return this.getCurrentUserContext();
    }
    if (normalized === 'currentUser.userId') {
      return this.getCurrentUserContext().userId;
    }
    if (normalized === 'currentUser.username') {
      return this.getCurrentUserContext().username;
    }
    if (normalized === 'currentUser.email') {
      return this.getCurrentUserContext().email;
    }

    return '';
  }

  private getCurrentUserContext(): SmartTableCurrentUserContext {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const email = this.firstString(
      tokenParsed?.email,
      typeof tokenParsed?.preferred_username === 'string' && tokenParsed.preferred_username.includes('@')
        ? tokenParsed.preferred_username
        : null,
    );

    return {
      userId: this.firstString(tokenParsed?.sub, keycloak?.subject),
      username: this.firstString(tokenParsed?.preferred_username, tokenParsed?.name, email),
      email,
    };
  }

  private firstString(...candidates: any[]): string {
    for (const candidate of candidates) {
      const value = String(candidate ?? '').trim();
      if (value) {
        return value;
      }
    }

    return '';
  }
}
